import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lumiFetch, lumiUpload, fmtDateTime, fmtDateTimeShort, toLocalInputValue } from "./lumiApi";
import { toast } from "./lumiToast";
import LumiInbox from "./LumiInbox";
import LumiSettings from "./LumiSettings";
import LumiDrips from "./LumiDrips";
import HotelPurchasesTab from "./HotelPurchasesTab";
import VerificationsTab from "./VerificationsTab";
import OrgSubscribersTab from "./OrgSubscribersTab";
import CreditUsageTab from "./CreditUsageTab";

const TABS = [
  { id: "compose", label: "Compose", icon: "✉️" },
  { id: "inbox", label: "Inbox", icon: "📥" },
  { id: "contacts", label: "Contacts", icon: "👥" },
  { id: "lists", label: "Lists", icon: "📋" },
  { id: "snippets", label: "Snippets", icon: "✂️" },
  { id: "drips", label: "Drips", icon: "🪴" },
  { id: "history", label: "History", icon: "📜" },
  { id: "purchases", label: "Purchases", icon: "🧾" },
  { id: "hotel-purchases", label: "Org Purchases", icon: "🏨" },
  { id: "credit-usage", label: "Credit Usage", icon: "📊" },
  { id: "verifications", label: "Verifications", icon: "🛡️" },
  { id: "org-subscribers", label: "Org Subscribers", icon: "📇" },
  { id: "visitors", label: "Visitors", icon: "🌍" },
  { id: "images", label: "Images", icon: "🖼️" },
  { id: "settings", label: "Settings", icon: "⚙️" },
];

const EMPTY_DRAFT = { subject: "", title: "", body_html: "", buttons: [], inline_image_urls: [], footer_image_url: "", video_url: "", attachments: [], theme: "dark" };

export default function LumiDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("compose");
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [draftId, setDraftId] = useState(null);
  // Last-saved snapshot — used to compute "● unsaved" indicator
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [sends, setSends] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [personaliseByAngle, setPersonaliseByAngle] = useState(false);
  const [images, setImages] = useState([]);

  // ---- Chat with Lumi ----
  const [chatSessionId] = useState(() => `chat_${Date.now()}`);
  const [messages, setMessages] = useState([
    { role: "lumi", content: "Hi Louise 💛 I'm Lumi. Tell me what kind of email you'd like to send today — a gentle introduction, a Poodle Talk update, news for SEND families… anything." },
  ]);
  const [input, setInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Voice input — Web Speech API, same pattern as Poodle Talk
  const toggleListening = () => {
    if (isListening) {
      try { recognitionRef.current?.stop(); } catch {}
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input isn't supported in this browser. Try Chrome or Safari.");
      return;
    }
    const r = new SpeechRecognition();
    r.lang = "en-GB";
    r.interimResults = true;
    r.continuous = false;
    r.maxAlternatives = 1;
    let finalText = "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      // Live update the input box as the user speaks
      setInput(((finalText + " " + interim).trim()));
    };
    r.onerror = (e) => {
      setIsListening(false);
      if (e.error === "not-allowed") toast.error("Mic permission denied. Allow microphone access in your browser.");
      else if (e.error !== "no-speech" && e.error !== "aborted") toast.error(`Voice error: ${e.error}`);
    };
    r.onend = () => setIsListening(false);
    recognitionRef.current = r;
    try {
      r.start();
      setIsListening(true);
    } catch (err) {
      toast.error("Could not start the microphone");
    }
  };

  // Stop listening if the component unmounts
  useEffect(() => () => { try { recognitionRef.current?.stop(); } catch {} }, []);

  // Load lists/drafts/contacts on mount
  useEffect(() => {
    refreshAll();
  }, []);

  const refreshAll = async () => {
    try {
      const [c, l, d, s, i, sch] = await Promise.all([
        lumiFetch("/contacts"),
        lumiFetch("/lists"),
        lumiFetch("/drafts"),
        lumiFetch("/sends"),
        lumiFetch("/images"),
        lumiFetch("/sends/scheduled"),
      ]);
      setContacts(c); setLists(l); setSavedDrafts(d); setSends(s); setImages(i); setScheduled(sch);
    } catch (e) {
      if (e.status === 401) onLogout?.();
    }
  };

  // Live server-side preview whenever draft changes (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await lumiFetch("/preview", { method: "POST", body: JSON.stringify(draft) });
        setPreviewHtml(r.html);
      } catch (e) { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [draft]);

  // ---- Chat send ----
  const sendChat = async () => {
    const text = input.trim();
    if (!text || chatBusy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setChatBusy(true);
    try {
      const res = await lumiFetch("/chat", {
        method: "POST",
        body: JSON.stringify({ session_id: chatSessionId, message: text, current_draft: draft }),
      });
      setMessages((m) => [...m, { role: "lumi", content: res.reply, hasDraft: !!res.draft_patch, fetchedUrls: res.fetched_urls || [] }]);
      if (res.draft_patch) {
        setDraft((d) => ({
          ...d,
          subject: res.draft_patch.subject ?? d.subject,
          title: res.draft_patch.title ?? d.title,
          body_html: res.draft_patch.body_html ?? d.body_html,
          buttons: res.draft_patch.buttons ?? d.buttons,
        }));
        toast.success("Lumi updated the draft 💌");
      }
    } catch (e) {
      toast.error(e?.data?.detail || "Lumi couldn't reply right now");
      setMessages((m) => [...m, { role: "lumi", content: "Sorry, I had trouble replying just now. Could you try again?" }]);
    } finally {
      setChatBusy(false);
    }
  };

  const onChatKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } };

  // ---- Draft actions ----
  const saveDraft = async (silent = false) => {
    try {
      const saved = draftId
        ? await lumiFetch(`/drafts/${draftId}`, { method: "PUT", body: JSON.stringify(draft) })
        : await lumiFetch(`/drafts`, { method: "POST", body: JSON.stringify(draft) });
      setDraftId(saved.id);
      setSavedSnapshot(JSON.stringify(draft));
      if (!silent) {
        toast.success("Draft saved → find it in History → Saved drafts");
        refreshAll();
      }
      return saved.id;
    } catch (e) {
      toast.error(e?.data?.detail || "Save failed");
      return null;
    }
  };

  const isDirty = savedSnapshot !== null && savedSnapshot !== JSON.stringify(draft);

  const newDraft = () => { setDraft(EMPTY_DRAFT); setDraftId(null); };

  const loadDraft = async (id) => {
    try {
      const d = await lumiFetch(`/drafts/${id}`);
      const loaded = {
        subject: d.subject || "", title: d.title || "", body_html: d.body_html || "",
        buttons: d.buttons || [], inline_image_urls: d.inline_image_urls || [],
        footer_image_url: d.footer_image_url || "",
        video_url: d.video_url || "",
        attachments: d.attachments || [],
        theme: d.theme || "dark",
      };
      setDraft(loaded);
      setSavedSnapshot(JSON.stringify(loaded));
      setDraftId(d.id);
      setTab("compose");
    } catch (e) { toast.error("Could not load draft"); }
  };

  const sendTest = async () => {
    if (!draft.subject || !draft.body_html) return toast.error("Subject and body are required");
    const target = user.test_recipient || user.email;
    if (!window.confirm(`Send a test of "${draft.subject}" to ${target}?`)) return;
    // Auto-save first so the test counts toward this exact draft for the broadcast guard.
    const id = await saveDraft(true);
    if (!id) return;
    try {
      const r = await lumiFetch("/send/test", { method: "POST", body: JSON.stringify({ draft, draft_id: id }) });
      toast.success(`Test sent to ${r.to || target} ✨`);
      refreshAll();
    } catch (e) { toast.error(e?.data?.detail || "Test send failed"); }
  };

  const sendBroadcast = async (listId) => {
    const list = lists.find((l) => l.id === listId);
    if (!list) return;
    const personaliseLabel = personaliseByAngle ? "\n\n✨ Personalise by recipient angle is ON — Lumi will rephrase the body to match each contact's email_angle." : "";
    if (!window.confirm(`BROADCAST "${draft.subject}" to ${list.contact_count} contacts in "${list.name}"? This cannot be undone.${personaliseLabel}`)) return;
    // Auto-save BEFORE broadcasting so what-you-see is what-gets-sent (test send already
    // uses the in-memory draft; this aligns broadcast with the same content).
    const id = await saveDraft(true);
    if (!id) return;
    try {
      const r = await lumiFetch("/send/broadcast", {
        method: "POST",
        body: JSON.stringify({ draft_id: id, list_id: listId, confirm: true, personalise_by_angle: personaliseByAngle }),
      });
      const dupeNote = r.skipped_dupe ? ` · ${r.skipped_dupe} skipped (already received this draft)` : "";
      toast.success(`Broadcast complete: ${r.sent} sent, ${r.failed} failed${dupeNote}`);
      refreshAll();
    } catch (e) { toast.error(e?.data?.detail || "Broadcast failed"); }
  };

  // ----- Audience (ad-hoc one-off send) state & handler -----
  // Pick a few existing contacts (or type Cc/Bcc emails) and fire ONE send
  // without creating/maintaining a list. Same safety guards as a full broadcast.
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceContactIds, setAudienceContactIds] = useState([]);
  const [audienceQuery, setAudienceQuery] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [bccEmails, setBccEmails] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const filteredAudienceContacts = useMemo(() => {
    const q = audienceQuery.trim().toLowerCase();
    const subscribedOnly = contacts.filter(
      (c) => c.subscribed !== false && c.include_in_broadcast !== false && (c.email || "").includes("@")
    );
    if (!q) return subscribedOnly.slice(0, 50);
    return subscribedOnly.filter((c) =>
      `${c.name || ""} ${c.email || ""} ${(c.org_names || []).join(" ")}`.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [contacts, audienceQuery]);

  const sendAudience = async () => {
    const cc = ccEmails.split(/[,\s\n]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
    const bcc = bccEmails.split(/[,\s\n]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
    const totalRecipients = audienceContactIds.length + cc.length + bcc.length;
    if (totalRecipients === 0) {
      toast.error("Pick at least one contact or type a Cc/Bcc email");
      return;
    }
    if (!window.confirm(
      `Send "${draft.subject}" to ${audienceContactIds.length} contact(s)` +
      (cc.length ? ` · cc ${cc.length}` : "") +
      (bcc.length ? ` · bcc ${bcc.length}` : "") +
      "?"
    )) return;
    const id = await saveDraft(true);
    if (!id) return;
    try {
      const r = await lumiFetch("/send/audience", {
        method: "POST",
        body: JSON.stringify({
          draft, draft_id: id,
          contact_ids: audienceContactIds,
          cc_emails: cc,
          bcc_emails: bcc,
          confirm: true,
        }),
      });
      const dupeNote = r.skipped_dupe ? ` · ${r.skipped_dupe} skipped (already received)` : "";
      toast.success(`Sent: ${r.sent} · failed: ${r.failed}${dupeNote}`);
      // Reset audience state on success
      setAudienceContactIds([]);
      setCcEmails("");
      setBccEmails("");
      setShowCc(false);
      setShowBcc(false);
      setAudienceOpen(false);
      refreshAll();
    } catch (e) { toast.error(e?.data?.detail || "Audience send failed"); }
  };

  const [showSchedule, setShowSchedule] = useState(null); // {listId, list} when picker open
  const [scheduleAt, setScheduleAt] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  // Snippets pickers in Compose: target is "chat" | "body" | null
  const [composeSnippetTarget, setComposeSnippetTarget] = useState(null);
  // Tab + search inside the compose snippet picker so it scales when you have many snippets.
  const [composeSnippetTab, setComposeSnippetTab] = useState("text"); // "text" | "video"
  const [composeSnippetQuery, setComposeSnippetQuery] = useState("");
  const [showVideoSnippets, setShowVideoSnippets] = useState(false);
  const [composeSnippets, setComposeSnippets] = useState([]); // [{kind, label, body}]
  const chatInputRef = useRef(null);
  const bodyRef = useRef(null);

  // Refresh snippets every time the picker is opened (so newly-added ones show without page reload)
  useEffect(() => {
    if (composeSnippetTarget || showVideoSnippets) {
      lumiFetch("/snippets").then(setComposeSnippets).catch(() => {});
      // Reset search & tab so each open is a clean slate
      setComposeSnippetQuery("");
      setComposeSnippetTab("text");
    }
  }, [composeSnippetTarget, showVideoSnippets]);

  const scheduleBroadcast = async (listId) => {
    const list = lists.find((l) => l.id === listId);
    if (!list) return;
    // Auto-save so the schedule grabs the latest content (theme, seasonal image, etc.)
    const id = await saveDraft(true);
    if (!id) return;
    // Default: tomorrow 09:00 local
    const def = new Date();
    def.setDate(def.getDate() + 1);
    def.setHours(9, 0, 0, 0);
    setScheduleAt(toLocalInputValue(def));
    setScheduleNote("");
    setShowSchedule({ listId, list, draftId: id });
  };

  const submitSchedule = async () => {
    const dt = new Date(scheduleAt);
    if (isNaN(dt.getTime())) return toast.error("Pick a valid date and time");
    if (dt <= new Date()) return toast.error("Pick a future time");
    try {
      await lumiFetch("/sends/schedule", {
        method: "POST",
        body: JSON.stringify({
          draft_id: showSchedule.draftId,
          list_id: showSchedule.listId,
          scheduled_at: dt.toISOString(),
          personalise_by_angle: personaliseByAngle,
          note: scheduleNote,
        }),
      });
      toast.success(`Scheduled for ${fmtDateTime(dt)} ⏰`);
      setShowSchedule(null);
      refreshAll();
    } catch (e) { toast.error(e?.data?.detail || "Could not schedule"); }
  };

  // ---- Buttons editor ----
  const addButton = () => setDraft((d) => ({ ...d, buttons: [...d.buttons, { text: "OPEN POODLE TALK", url: "https://poodletalk.poodlesandfriends.uk/" }] }));
  const updateButton = (i, k, v) => setDraft((d) => ({ ...d, buttons: d.buttons.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const removeButton = (i) => setDraft((d) => ({ ...d, buttons: d.buttons.filter((_, idx) => idx !== i) }));

  // ---- Attachments ----
  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const buf = await f.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = ""; for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const b64 = btoa(bin);
      setDraft((d) => ({ ...d, attachments: [...d.attachments, { filename: f.name, content_type: f.type || "application/octet-stream", content_b64: b64 }] }));
    }
    e.target.value = "";
  };
  const removeAttachment = (i) => setDraft((d) => ({ ...d, attachments: d.attachments.filter((_, idx) => idx !== i) }));

  // ---- Inline images (paste a URL) ----
  const addInlineImage = () => {
    const url = window.prompt("Paste image URL (must be a public URL):");
    if (url && url.startsWith("http")) {
      setDraft((d) => ({ ...d, inline_image_urls: [...d.inline_image_urls, url] }));
    }
  };
  const removeInlineImage = (i) => setDraft((d) => ({ ...d, inline_image_urls: d.inline_image_urls.filter((_, idx) => idx !== i) }));

  // ---- Footer image (single, centred, above footer) ----
  const uploadFooterImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await lumiUpload("/upload-image", fd);
      setDraft((d) => ({ ...d, footer_image_url: r.url }));
      toast.success("Image uploaded ✨");
      refreshAll();  // bring the new image into the library tab
    } catch (err) {
      toast.error(err?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };
  const clearFooterImage = () => setDraft((d) => ({ ...d, footer_image_url: "" }));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" data-testid="lumi-dashboard">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="text-xl">✨</div>
          <div>
            <div className="font-bold text-lg leading-tight">Lumi</div>
            <div className="text-xs text-white/50 leading-tight">Poodles &amp; Friends · marketing</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
          <span className="text-white/70 hidden sm:inline">{user.email}</span>
          <button onClick={onLogout} data-testid="lumi-logout" className="text-xs text-white/60 hover:text-white border border-white/20 rounded-full px-3 py-1">Sign out</button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex gap-1 px-4 pt-3 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`lumi-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm rounded-t-lg transition-all ${tab === t.id ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"}`}
          >
            <span className="mr-1">{t.icon}</span>{t.label}
          </button>
        ))}
      </nav>

      {tab === "compose" && (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[360px,1fr,520px] overflow-hidden">
          {/* Chat panel */}
          <aside className="border-r border-white/10 flex flex-col min-h-[400px]">
            <div className="px-4 py-3 text-xs uppercase tracking-wider text-white/40 border-b border-white/10">Chat with Lumi</div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                  <div className={`inline-block max-w-[85%] px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-white text-black" : "bg-white/10 text-white"}`}>
                    {m.content}
                    {m.hasDraft && <div className="text-xs mt-1 text-emerald-300">✓ updated the draft</div>}
                    {m.fetchedUrls && m.fetchedUrls.length > 0 && (
                      <div className="text-xs mt-1 space-y-0.5" data-testid="lumi-chat-fetched">
                        {m.fetchedUrls.filter((u) => u.ok).map((u) => (
                          <div key={u.url} className="text-sky-300 truncate" title={`Read ${u.chars} chars`}>🌐 read {u.url}</div>
                        ))}
                        {m.fetchedUrls.filter((u) => !u.ok).map((u) => (
                          <div key={u.url} className="text-orange-300 truncate" title={u.error}>⚠️ couldn't read {u.url}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {chatBusy && <div className="text-sm text-white/40 italic">Lumi is thinking…</div>}
              <div ref={chatEndRef} />
            </div>
            <div className="border-t border-white/10 p-3 flex gap-2 items-stretch">
              <button
                onClick={toggleListening}
                data-testid="lumi-chat-mic"
                title={isListening ? "Stop listening" : "Talk to Lumi"}
                className={`px-3 rounded-xl text-sm transition-all border ${isListening ? "bg-red-500 text-white border-red-400 animate-pulse" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
              >
                {isListening ? "🔴" : "🎤"}
              </button>
              <textarea
                data-testid="lumi-chat-input"
                ref={chatInputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onChatKey}
                rows={2}
                placeholder={isListening ? "🎙️ Listening… speak now" : 'e.g. "draft a gentle email about our new poodle Biscuit"'}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-white/30"
              />
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => setComposeSnippetTarget("chat")}
                  data-testid="lumi-chat-snippets-open"
                  title="Insert a saved snippet into the chat"
                  className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded-lg hover:bg-white/10"
                >
                  ✂️
                </button>
                <button data-testid="lumi-chat-send" onClick={sendChat} disabled={chatBusy} className="px-3 py-1 bg-white text-black rounded-lg text-xs font-medium hover:bg-white/90 disabled:opacity-50">Send</button>
              </div>
            </div>
          </aside>

          {/* Editor */}
          <section className="border-r border-white/10 overflow-y-auto p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-white/40">Editor</div>
              <div className="flex gap-2 items-center">
                {draftId && (
                  <span className="text-xs text-emerald-300/80 mr-1" data-testid="lumi-draft-saved-indicator">
                    ● Saved
                  </span>
                )}
                <div className="flex items-center gap-1 border border-white/20 rounded-full p-0.5" data-testid="lumi-theme-toggle">
                  <button
                    onClick={() => setDraft({ ...draft, theme: "dark" })}
                    data-testid="lumi-theme-dark"
                    className={`text-xs px-3 py-0.5 rounded-full transition-all ${draft.theme === "dark" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                    title="Black background, white text"
                  >
                    🌑 Dark
                  </button>
                  <button
                    onClick={() => setDraft({ ...draft, theme: "light" })}
                    data-testid="lumi-theme-light"
                    className={`text-xs px-3 py-0.5 rounded-full transition-all ${draft.theme === "light" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                    title="White background, black text (school-friendly)"
                  >
                    ☀️ Light
                  </button>
                  <button
                    onClick={() => setDraft({ ...draft, theme: "plain" })}
                    data-testid="lumi-theme-plain"
                    className={`text-xs px-3 py-0.5 rounded-full transition-all ${draft.theme === "plain" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                    title="Personal plain email — no logo banner, no big footer. Looks like a real person typed it. Best for school receptionists."
                  >
                    ✉️ Plain
                  </button>
                </div>
                <button onClick={newDraft} data-testid="lumi-new-draft" className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10">+ New</button>
                <button onClick={() => saveDraft(false)} data-testid="lumi-save-draft" className="text-xs bg-white/10 rounded-full px-3 py-1 hover:bg-white/20">
                  {isDirty && <span className="text-amber-300 mr-1" data-testid="lumi-unsaved-dot">●</span>}
                  Save draft
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-white/50">Subject</label>
              <input data-testid="lumi-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                placeholder="A gentle introduction to Poodles & Friends"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30" />
            </div>

            <div>
              <label className="text-xs text-white/50">Title (H1 inside the email)</label>
              <input data-testid="lumi-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Poodles and Friends News"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-white/50">Body (HTML — use &lt;p&gt; for paragraphs, &lt;strong&gt; for bold)</label>
                <button
                  onClick={() => setComposeSnippetTarget("body")}
                  data-testid="lumi-body-snippets-open"
                  title="Insert a saved snippet at cursor"
                  className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10"
                >
                  ✂️ Snippets
                </button>
              </div>
              <textarea data-testid="lumi-body" ref={bodyRef} value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                rows={14}
                placeholder="<p>Hello Friends,</p><p>I hope you're having a calm day…</p>"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-white/30" />
            </div>

            {/* Buttons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-white/50">Buttons (CTAs)</label>
                <button onClick={addButton} className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10">+ Add</button>
              </div>
              {draft.buttons.map((b, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={b.text} onChange={(e) => updateButton(i, "text", e.target.value)} placeholder="Text" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
                  <input value={b.url} onChange={(e) => updateButton(i, "url", e.target.value)} placeholder="https://…" className="flex-[2] bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
                  <button onClick={() => removeButton(i)} className="text-white/50 hover:text-white">✕</button>
                </div>
              ))}
            </div>

            {/* Inline images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-white/50">Inline images (full-width, by URL)</label>
                <button onClick={addInlineImage} className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10">+ Add URL</button>
              </div>
              {draft.inline_image_urls.map((u, i) => (
                <div key={i} className="flex gap-2 mb-1 items-center">
                  <span className="text-xs text-white/60 truncate flex-1">{u}</span>
                  <button onClick={() => removeInlineImage(i)} className="text-white/50 hover:text-white text-xs">✕</button>
                </div>
              ))}
            </div>

            {/* Footer image (single, centred, above footer) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-white/50">Seasonal image (centred, above the footer)</label>
                <div className="flex gap-2">
                  {draft.footer_image_url && (
                    <button onClick={clearFooterImage} className="text-xs text-white/50 hover:text-white">Remove</button>
                  )}
                  {images.length > 0 && (
                    <button
                      onClick={() => setTab("images")}
                      data-testid="lumi-pick-from-library"
                      className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10"
                    >
                      🗂️ Pick from library
                    </button>
                  )}
                  <label className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10 cursor-pointer">
                    {draft.footer_image_url ? "↻ Replace" : "+ Upload image"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadFooterImage} data-testid="lumi-footer-image-upload" />
                  </label>
                </div>
              </div>
              {draft.footer_image_url && (
                <div className="border border-white/10 rounded-lg p-2 bg-white/5">
                  <img src={draft.footer_image_url} alt="" className="max-h-32 mx-auto rounded" data-testid="lumi-footer-image-preview" />
                </div>
              )}
            </div>

            {/* Video link (YouTube preferred — renders as clickable thumbnail under the seasonal image) */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <label className="text-xs text-white/50">Video link (YouTube — appears under the seasonal image as a clickable thumbnail)</label>
                <button
                  onClick={() => setShowVideoSnippets(true)}
                  data-testid="lumi-video-snippets-open"
                  className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10 shrink-0"
                  title="Pick from saved video snippets"
                >
                  📺 From library
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={draft.video_url || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, video_url: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="lumi-video-url"
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                />
                {draft.video_url && (
                  <button onClick={() => setDraft((d) => ({ ...d, video_url: "" }))} className="text-xs text-white/40 hover:text-red-300 px-2">✕</button>
                )}
              </div>
              {draft.video_url && (() => {
                const m = (draft.video_url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/);
                const ytId = m ? m[1] : "";
                return (
                  <div className="mt-2 border border-white/10 rounded-lg p-2 bg-white/5 text-center" data-testid="lumi-video-preview">
                    {ytId ? (
                      <a href={draft.video_url} target="_blank" rel="noopener noreferrer">
                        <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt="Video preview" className="max-h-40 mx-auto rounded" />
                        <div className="text-xs text-white/60 mt-1">▶ Watch on YouTube</div>
                      </a>
                    ) : (
                      <div className="text-xs text-white/60">▶ Generic "Watch video" button (non-YouTube link)</div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-white/50">Attachments (PDF, image, doc — sent as file)</label>
                <label className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10 cursor-pointer">
                  + Upload
                  <input type="file" multiple className="hidden" onChange={onAttach} data-testid="lumi-attach-input" />
                </label>
              </div>
              {draft.attachments.map((a, i) => (
                <div key={i} className="flex gap-2 items-center text-xs text-white/70 mb-1">
                  <span>📎</span>
                  <span className="flex-1 truncate">{a.filename}</span>
                  <span className="text-white/40">{a.content_type}</span>
                  <button onClick={() => removeAttachment(i)} className="text-white/50 hover:text-white">✕</button>
                </div>
              ))}
            </div>

            {/* Send actions */}
            <div className="border-t border-white/10 pt-4 mt-4 space-y-3">
              <button data-testid="lumi-send-test" onClick={sendTest}
                className="w-full bg-amber-200 text-black rounded-xl py-3 text-sm font-medium hover:bg-amber-100">
                ✉️ Send test to me ({user.test_recipient || user.email})
              </button>
              <div className="text-xs text-white/40">Tip: send a test first, then choose a list to broadcast to.</div>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="lumi-personalise-toggle">
                <input
                  type="checkbox"
                  checked={personaliseByAngle}
                  onChange={(e) => setPersonaliseByAngle(e.target.checked)}
                  className="accent-emerald-300"
                />
                <span className={personaliseByAngle ? "text-emerald-200" : "text-white/70"}>
                  ✨ Personalise body & subject per recipient's email_angle
                </span>
                <span className="text-white/30 text-[10px]">(adds ~3-10s, 1 Claude call per unique angle)</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {/* Collapsed "Audience" send — pick contacts ad-hoc, no list needed */}
                <button
                  type="button"
                  onClick={() => setAudienceOpen((v) => !v)}
                  data-testid="lumi-audience-toggle"
                  className="text-xs bg-emerald-700/40 hover:bg-emerald-700/60 border border-emerald-300/30 rounded-full px-4 py-2 font-medium"
                >
                  🎯 Send to selected contacts {audienceContactIds.length > 0 && `(${audienceContactIds.length})`}
                </button>

                {/* Collapsed "Broadcast to a list" picker — replaces the giant button wall */}
                <BroadcastPicker
                  lists={lists}
                  onBroadcast={(id) => sendBroadcast(id)}
                  onSchedule={(id) => scheduleBroadcast(id)}
                />
              </div>

              {/* Audience panel — only renders when toggled open */}
              {audienceOpen && (
                <div className="border border-emerald-300/30 bg-emerald-700/10 rounded-xl p-3 space-y-3 mt-2" data-testid="lumi-audience-panel">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-emerald-200">🎯 One-off send (no list created)</p>
                    <button onClick={() => setAudienceOpen(false)} className="text-white/40 hover:text-white text-sm">✕</button>
                  </div>

                  {/* Contact picker */}
                  <div>
                    <input
                      type="text"
                      value={audienceQuery}
                      onChange={(e) => setAudienceQuery(e.target.value)}
                      placeholder="Search contacts by name, email or org…"
                      data-testid="lumi-audience-search"
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-300/40"
                    />
                    {audienceContactIds.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {audienceContactIds.map((id) => {
                          const c = contacts.find((x) => x.id === id);
                          if (!c) return null;
                          return (
                            <span key={id} className="bg-emerald-500/20 text-emerald-100 rounded-full pl-2 pr-1 py-0.5 text-[11px] inline-flex items-center gap-1">
                              {c.name || c.email}
                              <button
                                onClick={() => setAudienceContactIds((ids) => ids.filter((i) => i !== id))}
                                className="hover:bg-emerald-500/30 rounded-full w-4 h-4 flex items-center justify-center"
                              >×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="max-h-40 overflow-y-auto mt-2 bg-black/30 rounded-lg border border-white/5">
                      {filteredAudienceContacts.length === 0 ? (
                        <p className="text-xs text-white/40 px-3 py-2">No matching subscribed contacts.</p>
                      ) : (
                        filteredAudienceContacts.map((c) => {
                          const selected = audienceContactIds.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setAudienceContactIds((ids) =>
                                selected ? ids.filter((i) => i !== c.id) : [...ids, c.id]
                              )}
                              data-testid={`lumi-audience-contact-${c.id}`}
                              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-white/5 ${selected ? "bg-emerald-500/10" : ""}`}
                            >
                              <span className={`w-3 h-3 rounded border ${selected ? "bg-emerald-400 border-emerald-400" : "border-white/30"}`} />
                              <span className="flex-1 truncate">
                                <span className="text-white">{c.name || "(no name)"}</span>
                                <span className="text-white/40 ml-2">{c.email}</span>
                                {c.org_names?.length > 0 && (
                                  <span className="text-white/30 ml-2">· {c.org_names.join(", ")}</span>
                                )}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Cc / Bcc — collapsed by default to keep the panel tidy */}
                  <div className="flex gap-3 text-xs">
                    {!showCc && (
                      <button onClick={() => setShowCc(true)} className="text-emerald-200 hover:text-white" data-testid="lumi-audience-add-cc">+ Cc</button>
                    )}
                    {!showBcc && (
                      <button onClick={() => setShowBcc(true)} className="text-emerald-200 hover:text-white" data-testid="lumi-audience-add-bcc">+ Bcc</button>
                    )}
                  </div>
                  {showCc && (
                    <div>
                      <label className="text-[11px] text-white/50">Cc (comma-separated)</label>
                      <input
                        type="text"
                        value={ccEmails}
                        onChange={(e) => setCcEmails(e.target.value)}
                        placeholder="cc@example.com, another@example.com"
                        data-testid="lumi-audience-cc-input"
                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-300/40 mt-1"
                      />
                    </div>
                  )}
                  {showBcc && (
                    <div>
                      <label className="text-[11px] text-white/50">Bcc (comma-separated)</label>
                      <input
                        type="text"
                        value={bccEmails}
                        onChange={(e) => setBccEmails(e.target.value)}
                        placeholder="bcc@example.com, another@example.com"
                        data-testid="lumi-audience-bcc-input"
                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-300/40 mt-1"
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={sendAudience}
                    data-testid="lumi-audience-send"
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-medium rounded-lg py-2.5 text-sm"
                  >
                    🚀 Send now
                  </button>
                </div>
              )}
              {scheduled.filter((s) => s.status === "pending").length > 0 && (
                <div className="text-xs text-white/50 border-t border-white/10 pt-2" data-testid="lumi-scheduled-summary">
                  ⏰ {scheduled.filter((s) => s.status === "pending").length} scheduled — see <strong className="text-white/70">History tab</strong> to manage.
                </div>
              )}
            </div>
          </section>

          {/* Preview */}
          <section className="overflow-y-auto bg-white">
            <div className="px-4 py-2 text-xs uppercase tracking-wider text-black/40 border-b border-black/10 sticky top-0 bg-white">Live preview (what your reader will see)</div>
            <iframe title="email-preview" srcDoc={previewHtml} className="w-full" style={{ height: "calc(100vh - 130px)", border: "0" }} data-testid="lumi-preview-iframe" />
          </section>
        </div>
      )}

      {tab === "contacts" && (
        <ContactsTab contacts={contacts} lists={lists} refresh={refreshAll} />
      )}
      {tab === "lists" && (
        <ListsTab lists={lists} refresh={refreshAll} />
      )}
      {tab === "history" && (
        <HistoryTab sends={sends} scheduled={scheduled} drafts={savedDrafts} onLoadDraft={loadDraft} refresh={refreshAll} />
      )}
      {tab === "purchases" && <PurchasesTab />}
      {tab === "hotel-purchases" && <HotelPurchasesTab />}
      {tab === "credit-usage" && <CreditUsageTab />}
      {tab === "verifications" && <VerificationsTab />}
      {tab === "org-subscribers" && <OrgSubscribersTab />}
      {tab === "visitors" && <VisitorsTab />}
      {tab === "snippets" && <SnippetsTab />}
      {tab === "drips" && <LumiDrips />}

      {/* Compose snippet picker — inserts into chat input or body editor based on target */}
      {composeSnippetTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { setComposeSnippetTarget(null); setComposeSnippetQuery(""); } }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" data-testid="lumi-compose-snippets-modal">
            <div className="px-5 py-3 border-b border-white/10">
              <div className="font-bold">Insert snippet into {composeSnippetTarget === "chat" ? "chat with Lumi" : "email body"}</div>
              <div className="text-xs text-white/50 mt-1">Click a snippet to insert it at your cursor.</div>
              {/* Tab switcher — body picker is text-only, chat shows both kinds */}
              <div className="flex gap-1.5 mt-3">
                <button
                  onClick={() => setComposeSnippetTab("text")}
                  data-testid="lumi-compose-snippet-tab-text"
                  className={`text-xs px-3 py-1 rounded-full border transition-all ${composeSnippetTab === "text" ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
                >
                  📋 Text
                </button>
                {composeSnippetTarget === "chat" && (
                  <button
                    onClick={() => setComposeSnippetTab("video")}
                    data-testid="lumi-compose-snippet-tab-video"
                    className={`text-xs px-3 py-1 rounded-full border transition-all ${composeSnippetTab === "video" ? "bg-rose-300/30 text-rose-50 border-rose-300/60" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
                  >
                    📺 Video
                  </button>
                )}
                <input
                  type="search"
                  value={composeSnippetQuery}
                  onChange={(e) => setComposeSnippetQuery(e.target.value)}
                  placeholder="🔍 Search snippets…"
                  data-testid="lumi-compose-snippet-search"
                  autoFocus
                  className="flex-1 ml-1 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs focus:outline-none focus:border-white/30"
                />
              </div>
            </div>
          <div className="flex-1 overflow-y-auto divide-y divide-white/5">
            {(() => {
              // body picker: text-only (videos go via the dedicated Video URL field)
              // chat picker: filtered by the selected tab
              const wantedKind = composeSnippetTarget === "body" ? "text" : composeSnippetTab;
              const q = composeSnippetQuery.trim().toLowerCase();
              let visible = composeSnippets.filter((s) => (s.kind || "text") === wantedKind);
              if (q) {
                visible = visible.filter((s) =>
                  (s.label || "").toLowerCase().includes(q) ||
                  (s.body || "").toLowerCase().includes(q)
                );
                // Sort matches that START with the query first (so typing "C" puts "Credit"-prefixed labels at the top)
                visible.sort((a, b) => {
                  const al = (a.label || "").toLowerCase();
                  const bl = (b.label || "").toLowerCase();
                  const ap = al.startsWith(q) ? 0 : 1;
                  const bp = bl.startsWith(q) ? 0 : 1;
                  return ap !== bp ? ap - bp : al.localeCompare(bl);
                });
              }
              if (visible.length === 0) {
                return (
                  <div className="p-6 text-center text-xs text-white/40">
                    {q
                      ? <>No {wantedKind} snippets match <strong className="text-white/70">"{composeSnippetQuery}"</strong>.</>
                      : <>No {wantedKind} snippets yet — create some in the <strong className="text-white/60">Snippets</strong> tab.</>
                    }
                  </div>
                );
              }
              return visible.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    const ref = composeSnippetTarget === "chat" ? chatInputRef.current : bodyRef.current;
                    if (composeSnippetTarget === "chat") {
                      const v = input || "";
                      const start = ref?.selectionStart ?? v.length;
                      const end = ref?.selectionEnd ?? v.length;
                      setInput(v.slice(0, start) + s.body + v.slice(end));
                      setTimeout(() => { ref?.focus(); const p = start + s.body.length; ref?.setSelectionRange?.(p, p); }, 0);
                    } else {
                      const v = draft.body_html || "";
                      const start = ref?.selectionStart ?? v.length;
                      const end = ref?.selectionEnd ?? v.length;
                      setDraft((d) => ({ ...d, body_html: v.slice(0, start) + s.body + v.slice(end) }));
                      setTimeout(() => { ref?.focus(); const p = start + s.body.length; ref?.setSelectionRange?.(p, p); }, 0);
                    }
                    setComposeSnippetTarget(null);
                    setComposeSnippetQuery("");
                    toast.success(`Inserted "${s.label}"`);
                  }}
                  data-testid={`lumi-compose-snippet-${s.id}`}
                  className="w-full text-left px-4 py-3 hover:bg-white/5"
                >
                  <div className="flex items-center gap-2">
                    {s.kind === "video" && <span className="text-[10px] bg-rose-300/20 border border-rose-300/40 text-rose-100 rounded px-1.5 py-0.5 shrink-0">📺 video</span>}
                    <div className="text-sm font-medium truncate">{s.label}</div>
                  </div>
                  <div className="text-xs text-white/50 line-clamp-2 whitespace-pre-wrap mt-0.5">{s.body}</div>
                </button>
              ));
            })()}
          </div>
            <div className="px-5 py-3 border-t border-white/10 text-right">
              <button onClick={() => setComposeSnippetTarget(null)} className="text-xs text-white/60 hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Video snippet picker — sets draft.video_url */}
      {showVideoSnippets && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowVideoSnippets(false); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" data-testid="lumi-video-snippets-modal">
            <div className="px-5 py-3 border-b border-white/10">
              <div className="font-bold">📺 Pick a video link</div>
              <div className="text-xs text-white/50 mt-1">Click to set as the email's video. Manage in the <strong className="text-white/70">Snippets</strong> tab → Video.</div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-white/5">
              {composeSnippets.filter((s) => s.kind === "video").length === 0 ? (
                <div className="p-6 text-center text-xs text-white/40">No video snippets yet — create some in the <strong className="text-white/60">Snippets → Video</strong> tab.</div>
              ) : composeSnippets.filter((s) => s.kind === "video").map((s) => {
                const m = (s.body || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/);
                const ytId = m ? m[1] : "";
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      setDraft((d) => ({ ...d, video_url: s.body }));
                      setShowVideoSnippets(false);
                      toast.success(`Video set: "${s.label}"`);
                    }}
                    data-testid={`lumi-video-snippet-${s.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-center gap-3"
                  >
                    {ytId && <img src={`https://img.youtube.com/vi/${ytId}/default.jpg`} alt="" className="w-20 h-14 rounded object-cover shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{s.label}</div>
                      <div className="text-xs text-white/50 truncate">{s.body}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-white/10 text-right">
              <button onClick={() => setShowVideoSnippets(false)} className="text-xs text-white/60 hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule picker modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowSchedule(null); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="lumi-schedule-modal">
            <div>
              <div className="font-bold">Schedule broadcast</div>
              <div className="text-xs text-white/50 mt-1">
                "{draft.subject || '(no subject)'}" → <strong className="text-white/80">{showSchedule.list.name}</strong> ({showSchedule.list.contact_count} contacts)
                {personaliseByAngle && <span className="ml-2 text-emerald-300">✨ personalised</span>}
              </div>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">When (your local time, 24-hour)</label>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                data-testid="lumi-schedule-at"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Note (optional)</label>
              <input
                type="text"
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                placeholder="A reminder for your future self"
                data-testid="lumi-schedule-note"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setShowSchedule(null)} className="text-xs px-3 py-1.5 border border-white/20 rounded-lg hover:bg-white/10">Cancel</button>
              <button onClick={submitSchedule} data-testid="lumi-schedule-submit" className="text-xs px-3 py-1.5 bg-white text-black rounded-lg font-medium">Schedule ⏰</button>
            </div>
          </div>
        </div>
      )}
      {tab === "images" && (
        <ImagesTab images={images} refresh={refreshAll} onPickAsFooter={(url) => { setDraft((d) => ({ ...d, footer_image_url: url })); setTab("compose"); toast.success("Image set as seasonal image ✨"); }} />
      )}
      {tab === "inbox" && <LumiInbox />}
      {tab === "settings" && <LumiSettings />}
    </div>
  );
}

// ----- Sub tabs -----
function ContactsTab({ contacts, lists, refresh }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [listIds, setListIds] = useState([]);  // multi-select
  const [notes, setNotes] = useState("");
  const [emailAngles, setEmailAngles] = useState([]);  // multi-tick; first is primary
  const [sendKidsCount, setSendKidsCount] = useState("");  // string in input, parsed to int on send
  const [websites, setWebsites] = useState([]);  // list of URLs related to this contact
  const [orgIds, setOrgIds] = useState([]);  // chosen orgs for the new contact (multi-org)
  const [includeBroadcast, setIncludeBroadcast] = useState(true);  // per-contact broadcast toggle
  const [angles, setAngles] = useState([]);
  const [editing, setEditing] = useState(null); // contact being edited
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");  // "date" | "send_kids_desc"
  const [researching, setResearching] = useState(false);
  // Orgs — full local list, plus an inline editor for create/rename/research.
  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState("");  // "" = all, "_none" = no-org, else org id
  const [orgEditor, setOrgEditor] = useState(null);  // {id?, name, websites[], notes, color}
  const [researchingOrgId, setResearchingOrgId] = useState(null);

  useEffect(() => {
    lumiFetch("/contacts/email-angles").then((r) => setAngles(r.angles || [])).catch(() => {});
  }, []);

  const refreshOrgs = useCallback(async () => {
    try { setOrgs(await lumiFetch("/orgs") || []); } catch {}
  }, []);
  useEffect(() => { refreshOrgs(); }, [refreshOrgs]);

  const total = contacts.length;
  const subscribed = contacts.filter((c) => c.subscribed !== false).length;
  const unsubscribed = total - subscribed;

  const toggleList = (id) => {
    setListIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAngle = (a) => {
    setEmailAngles((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  };

  const toggleOrgIdAdd = (id) => {
    setOrgIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleOrgIdEdit = (id) => {
    setEditing((ed) => {
      const cur = ed.org_ids || (ed.org_id ? [ed.org_id] : []);
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...ed, org_ids: next, org_id: next[0] || null };
    });
  };

  const add = async () => {
    // Email is now optional — name-only contacts allowed for prospects whose
    // email Louise hasn't found yet. Require at least one of email or name.
    if (!email && !name) return toast.error("Add an email OR a name");
    try {
      const kids = sendKidsCount.trim() === "" ? null : parseInt(sendKidsCount, 10);
      await lumiFetch("/contacts", { method: "POST", body: JSON.stringify({
        email: email || null,
        name,
        list_ids: listIds,
        notes,
        email_angles: emailAngles,
        send_kids_count: Number.isFinite(kids) ? Math.max(0, kids) : null,
        websites,
        org_ids: orgIds,
        include_in_broadcast: includeBroadcast,
      }) });
      setEmail(""); setName(""); setListIds([]); setNotes("");
      setEmailAngles([]); setSendKidsCount(""); setWebsites([]);
      setOrgIds([]); setIncludeBroadcast(true);
      toast.success("Contact added");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not add contact"); }
  };

  // Quick org helpers used by the editor + filter chips
  const saveOrg = async () => {
    if (!orgEditor || !orgEditor.name?.trim()) return toast.error("Org name required");
    const body = {
      name: orgEditor.name.trim(),
      websites: orgEditor.websites || [],
      notes: orgEditor.notes || "",
      color: orgEditor.color || null,
    };
    try {
      let saved;
      if (orgEditor.id) {
        saved = await lumiFetch(`/orgs/${orgEditor.id}`, { method: "PUT", body: JSON.stringify(body) });
        toast.success("Org saved");
      } else {
        saved = await lumiFetch("/orgs", { method: "POST", body: JSON.stringify(body) });
        toast.success(`Created "${saved.name}"`);
      }
      setOrgEditor(null);
      // If we're inside the contact editor, append the just-saved org id (don't replace existing).
      if (editing && !orgEditor.id) {
        setEditing((ed) => {
          const cur = ed.org_ids || (ed.org_id ? [ed.org_id] : []);
          if (cur.includes(saved.id)) return ed;
          const next = [...cur, saved.id];
          return { ...ed, org_ids: next, org_id: next[0] };
        });
      } else if (!editing && !orgEditor.id) {
        setOrgIds((prev) => prev.includes(saved.id) ? prev : [...prev, saved.id]);
      }
      refreshOrgs();
    } catch (e) {
      // Surface the real reason. e.data.detail = backend FastAPI HTTPException detail;
      // e.message = HTTP-level fallback (e.g. "HTTP 404"); status helps narrow it down.
      const detail = e?.data?.detail;
      const status = e?.status;
      const msg = detail
        ? `Could not save org: ${detail}`
        : status
          ? `Could not save org (HTTP ${status}). Try again or refresh the page.`
          : `Could not save org: ${e?.message || "network error"}`;
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("[saveOrg] failed", { status, detail, error: e });
    }
  };

  const deleteOrg = async (orgIdToDelete) => {
    const o = orgs.find((x) => x.id === orgIdToDelete);
    if (!o) return;
    const msg = (o.contact_count || 0) > 0
      ? `Delete "${o.name}"? Its ${o.contact_count} contact(s) will become "no org" (we won't delete the contacts).`
      : `Delete "${o.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await lumiFetch(`/orgs/${orgIdToDelete}`, { method: "DELETE" });
      toast.success("Org deleted");
      refreshOrgs();
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not delete"); }
  };

  const researchOrg = async (org) => {
    setResearchingOrgId(org.id);
    try {
      const r = await lumiFetch(`/orgs/${org.id}/research`, { method: "POST" });
      toast.success("🔬 Lumi has researched this org");
      refreshOrgs();
      // Show the result inline by opening the editor with the new summary
      const fresh = await lumiFetch("/orgs");
      const updated = (fresh || []).find((x) => x.id === org.id);
      if (updated) setOrgEditor({ ...updated, _showResearch: true });
      else if (r?.summary) setOrgEditor({ ...org, research_summary: r.summary, _showResearch: true });
    } catch (e) { toast.error(e?.data?.detail || "Research failed"); }
    finally { setResearchingOrgId(null); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    // For CSV import we still pass a single list_id (to keep import simple);
    // ticking multiple lists then importing applies the FIRST list to all imported rows.
    fd.append("list_id", listIds[0] || "");
    try {
      const r = await lumiUpload("/contacts/import", fd);
      const msg = r.merged_duplicates > 0
        ? `Imported ${r.imported} rows → merged into ${r.unique_contacts} unique contacts. ${r.merged_duplicates} duplicate-email rows were combined (only the LAST row's name/notes/angle was kept per email).`
        : `Imported ${r.imported} (skipped ${r.skipped})`;
      toast.success(msg, { duration: r.merged_duplicates > 0 ? 12000 : 4000 });
      refresh();
    } catch (err) { toast.error("Import failed"); }
    e.target.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this contact?")) return;
    await lumiFetch(`/contacts/${id}`, { method: "DELETE" });
    refresh();
  };

  const resubscribe = async (c) => {
    if (!window.confirm(`Re-subscribe ${c.email}?\n\nOnly do this if they have explicitly asked to be added back.`)) return;
    try {
      await lumiFetch(`/contacts/${c.id}/resubscribe`, { method: "POST" });
      toast.success("Resubscribed ✨");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not resubscribe"); }
  };

  // Save edits to an existing contact's list memberships
  const saveEdit = async () => {
    try {
      const newEmail = (editing.email || "").trim().toLowerCase();
      const origEmail = (editing._origEmail || "").trim().toLowerCase();
      // If the email itself changed, hit the dedicated endpoint first so we
      // update the row in-place (rather than creating a brand-new contact).
      if (newEmail && newEmail !== origEmail) {
        if (!window.confirm(`Change email from "${origEmail}" to "${newEmail}"?`)) return;
        await lumiFetch(`/contacts/${editing.id}/email`, {
          method: "PUT",
          body: JSON.stringify({ email: newEmail }),
        });
      }
      const kids = (editing.send_kids_count === null || editing.send_kids_count === undefined || editing.send_kids_count === "")
        ? null
        : parseInt(editing.send_kids_count, 10);
      // Now upsert the rest of the fields (keyed by id so no-email contacts update in place)
      await lumiFetch("/contacts", {
        method: "POST",
        body: JSON.stringify({
          id: editing.id,
          email: newEmail || origEmail || null,
          name: editing.name || "",
          list_ids: editing.list_ids || [],
          notes: editing.notes || "",
          email_angles: editing.email_angles || [],
          send_kids_count: Number.isFinite(kids) ? Math.max(0, kids) : null,
          websites: editing.websites || [],
          org_ids: editing.org_ids || (editing.org_id ? [editing.org_id] : []),
          include_in_broadcast: editing.include_in_broadcast !== false,
        }),
      });
      toast.success(newEmail !== origEmail ? "Email + details updated" : "Updated");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not update"); }
  };

  // One-click toggle of the ★ flag on a contact (independent of the Edit modal).
  const toggleStar = async (c) => {
    try {
      await lumiFetch(`/contacts/${c.id}/star`, { method: "POST" });
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Couldn't toggle star"); }
  };

  // Lumi reads the contact's saved websites and writes a 3-6 line research summary.
  const researchContact = async () => {
    if (!editing?.id) return;
    if (!(editing.websites || []).length) {
      toast.error("Add at least one website first, then click Research.");
      return;
    }
    setResearching(true);
    try {
      const r = await lumiFetch(`/contacts/${editing.id}/research`, { method: "POST" });
      setEditing((ed) => ({
        ...ed,
        research_summary: r.summary || "",
        research_at: r.researched_at || new Date().toISOString(),
      }));
      const okCount = (r.fetched || []).filter((f) => f.ok).length;
      toast.success(`Research saved! Read ${okCount}/${(r.fetched || []).length} pages 🔍`);
      refresh();
    } catch (e) {
      toast.error(e?.data?.detail || "Could not research this contact");
    } finally {
      setResearching(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Contacts</h2>
        <div className="flex gap-3 text-xs" data-testid="lumi-contacts-stats">
          <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <div className="text-white/40 uppercase text-[10px] tracking-wider">Total</div>
            <div className="text-white font-bold text-lg leading-none mt-0.5">{total}</div>
          </div>
          <div className="bg-emerald-300/10 border border-emerald-300/20 rounded-lg px-3 py-2" data-testid="lumi-stats-subscribed">
            <div className="text-emerald-200/70 uppercase text-[10px] tracking-wider">Subscribed</div>
            <div className="text-emerald-200 font-bold text-lg leading-none mt-0.5">{subscribed}</div>
          </div>
          <div className="bg-rose-300/10 border border-rose-300/20 rounded-lg px-3 py-2" data-testid="lumi-stats-unsubscribed">
            <div className="text-rose-200/70 uppercase text-[10px] tracking-wider">Unsubscribed</div>
            <div className="text-rose-200 font-bold text-lg leading-none mt-0.5">{unsubscribed}</div>
          </div>
        </div>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2">
          <input data-testid="lumi-contact-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com (optional)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="lumi-contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Mrs Smith)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="lumi-contact-add" onClick={add} className="bg-white text-black rounded-lg px-4 py-2 text-sm font-medium">Add</button>
        </div>

        {/* Org picker (multi) + include-in-broadcast toggle on the add form */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-white/50">🏢 Orgs:</span>
          {orgs.length === 0 && (
            <span className="text-xs text-white/35 italic">No orgs yet — click "+ New org" to add one</span>
          )}
          {orgs.map((o) => {
            const on = orgIds.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => toggleOrgIdAdd(o.id)}
                data-testid={`lumi-contact-org-pick-${o.id}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${on ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/70 hover:text-white"}`}
              >
                {on ? "✓ " : ""}{o.name}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setOrgEditor({ id: null, name: "", websites: [], notes: "", color: null })}
            data-testid="lumi-contact-org-new"
            className="text-xs px-2 py-1.5 bg-white/5 border border-dashed border-white/20 rounded-lg hover:bg-white/10 text-white/60 hover:text-white"
          >+ New org</button>
          <label className="flex items-center gap-1.5 text-xs text-white/70 ml-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeBroadcast}
              onChange={(e) => setIncludeBroadcast(e.target.checked)}
              data-testid="lumi-contact-include-broadcast"
              className="accent-emerald-300"
            />
            Include in broadcasts &amp; drips
          </label>
        </div>
        {lists.length > 0 && (
          <div>
            <div className="text-xs text-white/50 mb-1.5">Tick the lists this contact belongs to (you can pick more than one):</div>
            <div className="flex flex-wrap gap-2">
              {lists.map((l) => {
                const on = listIds.includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggleList(l.id)}
                    data-testid={`lumi-contact-list-${l.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                      on
                        ? "bg-emerald-300/20 border-emerald-300/40 text-emerald-100"
                        : "bg-white/5 border-white/15 text-white/60 hover:text-white"
                    }`}
                  >
                    {on ? "✓ " : ""}{l.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-[1fr,1fr]">
          <div>
            <div className="text-xs text-white/50 mb-1.5">
              Email angles — tick all that apply (the <strong>first one ticked</strong> = primary; Lumi tunes drafts to that tone first):
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[...angles, ...emailAngles.filter((a) => !angles.includes(a))].map((a) => {
                const idx = emailAngles.indexOf(a);
                const on = idx >= 0;
                const isPrimary = idx === 0;
                const isCustom = !angles.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAngle(a)}
                    data-testid={`lumi-contact-angle-${a}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                      isPrimary
                        ? "bg-sky-300/30 border-sky-300/60 text-sky-50"
                        : on
                          ? "bg-sky-300/15 border-sky-300/40 text-sky-100"
                          : isCustom
                            ? "bg-amber-300/10 border-amber-300/30 text-amber-100/80 hover:text-amber-50"
                            : "bg-white/5 border-white/15 text-white/60 hover:text-white"
                    }`}
                    title={isCustom ? "Custom angle" : ""}
                  >
                    {isPrimary ? "★ " : on ? "✓ " : ""}{isCustom && !on ? "✎ " : ""}{a}
                  </button>
                );
              })}
            </div>
            <div className="mt-2">
              <input
                type="text"
                placeholder="+ Add a custom angle (press Enter)"
                data-testid="lumi-contact-custom-angle"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value.trim()) {
                    const newAngle = e.currentTarget.value.trim();
                    if (!emailAngles.includes(newAngle)) {
                      setEmailAngles((prev) => [...prev, newAngle]);
                    }
                    e.currentTarget.value = "";
                    e.preventDefault();
                  }
                }}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-amber-300/40"
              />
            </div>
          </div>
          <div className="grid grid-cols-[1fr,140px] gap-2 items-end">
            <div>
              <div className="text-xs text-white/50 mb-1">Notes (private — Lumi reads these)</div>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="lumi-contact-notes"
                placeholder="e.g. Met at SEND fair Sept 2025, prefers Mondays"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <div className="text-xs text-white/50 mb-1" title="For schools — how many SEND kids on roll. Helps you prioritise outreach.">🎯 SEND kids count</div>
              <input
                type="number"
                min="0"
                value={sendKidsCount}
                onChange={(e) => setSendKidsCount(e.target.value)}
                data-testid="lumi-contact-send-kids"
                placeholder="e.g. 300"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs text-white/50 mb-1.5">🔗 Websites (links related to this contact — press Enter to add)</div>
          <WebsiteListEditor websites={websites} onChange={setWebsites} testIdPrefix="lumi-contact" />
        </div>
      </div>
      <div className="mb-4">
        <label className="inline-block text-xs border border-white/20 rounded-full px-3 py-2 cursor-pointer hover:bg-white/10">
          📥 Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="lumi-import-csv" />
        </label>
        <span className="text-xs text-white/40 ml-3">
          Columns: <code className="text-white/60">email, name, list_ids, notes, email_angles, send_kids_count, websites</code>
          {" "}(<code>email_angles</code> &amp; <code>websites</code> semicolon-separated; legacy <code>email_angle</code> still accepted). Already-unsubscribed emails are skipped.
        </span>
      </div>
      <div className="mb-3 grid gap-2 sm:grid-cols-[1fr,auto]">
        <input
          type="search"
          placeholder="🔍 Search contacts (email, name, notes, list, angle, SEND count…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="lumi-contacts-search"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-white/30"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="lumi-contacts-sort"
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
          title="Sort contacts"
        >
          <option value="date">↧ Newest first</option>
          <option value="send_kids_desc">🎯 Most SEND kids first</option>
        </select>
      </div>
      {search.trim() && (
        <div className="text-xs text-white/40 mb-3 ml-1">
          {(() => {
            const q = search.trim().toLowerCase();
            const matched = contacts.filter((c) => {
              const haystacks = [
                (c.email || "").toLowerCase(),
                (c.name || "").toLowerCase(),
                (c.notes || "").toLowerCase(),
                ...(c.email_angles || (c.email_angle ? [c.email_angle] : [])).map((a) => (a || "").toLowerCase()),
                String(c.send_kids_count ?? ""),
                ...(c.list_ids || []).map((id) => lists.find((l) => l.id === id)?.name?.toLowerCase() || ""),
              ];
              return haystacks.some((h) => h.includes(q));
            }).length;
            return `${matched} of ${contacts.length} match`;
          })()}
          <button
            onClick={() => setSearch("")}
            className="ml-3 underline hover:text-white/70"
            data-testid="lumi-contacts-clear-search"
          >
            clear
          </button>
        </div>
      )}

      {/* Org filter chips — quick "show only this school" */}
      {orgs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="lumi-contacts-org-filter">
          <span className="text-xs text-white/50 self-center mr-1">🏢 Org:</span>
          <button
            onClick={() => setOrgFilter("")}
            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${orgFilter === "" ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
          >All</button>
          <button
            onClick={() => setOrgFilter("_none")}
            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${orgFilter === "_none" ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
          >No org</button>
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setOrgFilter(orgFilter === o.id ? "" : o.id)}
              data-testid={`lumi-contacts-org-chip-${o.id}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${orgFilter === o.id ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
              title={`${o.contact_count || 0} contact(s)`}
            >
              {o.name}{o.contact_count ? ` · ${o.contact_count}` : ""}
            </button>
          ))}
          <button
            onClick={() => setOrgEditor({ id: null, name: "", websites: [], notes: "", color: null })}
            className="text-xs px-2.5 py-1 rounded-full border border-dashed border-white/20 text-white/40 hover:text-white"
          >+ New org</button>
        </div>
      )}
      <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
        {contacts.length === 0 && <div className="p-6 text-center text-white/40 text-sm">No contacts yet.</div>}
        {(() => {
          const q = search.trim().toLowerCase();
          let rows = contacts;
          // Org filter — "" = all, "_none" = no org, else specific org id (matches array OR legacy single)
          const orgIdsOf = (c) => c.org_ids && c.org_ids.length ? c.org_ids : (c.org_id ? [c.org_id] : []);
          if (orgFilter === "_none") {
            rows = rows.filter((c) => orgIdsOf(c).length === 0);
          } else if (orgFilter) {
            rows = rows.filter((c) => orgIdsOf(c).includes(orgFilter));
          }
          if (q) {
            rows = rows.filter((c) => {
              const orgNames = orgIdsOf(c).map((id) => (orgs.find((o) => o.id === id)?.name || "").toLowerCase());
              const haystacks = [
                (c.email || "").toLowerCase(),
                (c.name || "").toLowerCase(),
                (c.notes || "").toLowerCase(),
                ...(c.email_angles || (c.email_angle ? [c.email_angle] : [])).map((a) => (a || "").toLowerCase()),
                String(c.send_kids_count ?? ""),
                ...(c.list_ids || []).map((id) => lists.find((l) => l.id === id)?.name || "").map((s) => s.toLowerCase()),
                c.starred ? "starred star" : "",
                ...orgNames,
              ];
              return haystacks.some((h) => h.includes(q));
            });
          }
          if (sortBy === "send_kids_desc") {
            rows = [...rows].sort((a, b) => (b.send_kids_count ?? -1) - (a.send_kids_count ?? -1));
          }
          return rows;
        })().map((c) => {
          const isUnsub = c.subscribed === false;
          const memberLists = (c.list_ids || []).map((id) => lists.find((l) => l.id === id)).filter(Boolean);
          const contactAngles = c.email_angles && c.email_angles.length
            ? c.email_angles
            : (c.email_angle ? [c.email_angle] : []);
          return (
            <div key={c.id} className={`px-4 py-3 flex items-start gap-3 text-sm ${isUnsub ? "opacity-60" : ""}`} data-testid={`lumi-contact-row-${c.id}`}>
              <button
                type="button"
                onClick={() => toggleStar(c)}
                data-testid={`lumi-star-${c.id}`}
                title={c.starred ? "★ Starred — Lumi uses Louise's personal sign-off for this contact. Click to unstar." : "Click to star — Lumi will reply as Louise (founder sign-off) for this contact."}
                className={`mt-0.5 text-lg leading-none transition-transform hover:scale-110 shrink-0 ${c.starred ? "text-amber-300 drop-shadow-[0_0_6px_rgba(252,211,77,0.6)]" : "text-white/15 hover:text-white/40"}`}
              >
                {c.starred ? "★" : "☆"}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="truncate">{c.email || <span className="italic text-white/40">no email yet</span>}</span>
                  {isUnsub && (
                    <span className="text-[10px] bg-rose-300/20 text-rose-200 rounded px-1.5 py-0.5 shrink-0" data-testid={`lumi-unsubbed-badge-${c.id}`}>
                      unsubscribed
                    </span>
                  )}
                  {c.include_in_broadcast === false && (
                    <span
                      className="text-[10px] bg-amber-300/20 text-amber-100 border border-amber-300/40 rounded px-1.5 py-0.5 shrink-0"
                      data-testid={`lumi-no-broadcast-${c.id}`}
                      title="This contact won't receive broadcasts or drips"
                    >
                      🔕 no broadcast
                    </span>
                  )}
                  {(() => {
                    const cOrgIds = c.org_ids && c.org_ids.length ? c.org_ids : (c.org_id ? [c.org_id] : []);
                    return cOrgIds.map((oid) => {
                      const org = orgs.find((o) => o.id === oid);
                      if (!org) return null;
                      return (
                        <button
                          key={oid}
                          onClick={() => setOrgFilter(org.id === orgFilter ? "" : org.id)}
                          className="text-[10px] bg-violet-300/15 text-violet-100 border border-violet-300/40 rounded px-1.5 py-0.5 shrink-0 hover:bg-violet-300/25"
                          data-testid={`lumi-contact-org-chip-${c.id}-${oid}`}
                          title="Click to filter contacts in this org"
                        >
                          🏢 {org.name}
                        </button>
                      );
                    });
                  })()}
                </div>
                <div className="text-xs text-white/50">{c.name || "—"}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {memberLists.length === 0 && <span className="text-[10px] text-white/30 italic">no list</span>}
                  {memberLists.map((l) => {
                    // Tinted chip when the list has a colour set; neutral grey when not.
                    // Background uses ~22% alpha + matching border so the chip stays readable on dark bg.
                    const c = l.color;
                    const style = c ? { backgroundColor: `${c}38`, borderColor: `${c}90`, color: "#fff" } : undefined;
                    return (
                      <span
                        key={l.id}
                        className={`text-[10px] rounded px-1.5 py-0.5 border ${c ? "" : "bg-white/5 border-white/15 text-white/70"}`}
                        style={style}
                        title={l.description || l.name}
                      >
                        {l.name}
                      </span>
                    );
                  })}
                  {contactAngles.map((a, i) => (
                    <span
                      key={`a-${i}`}
                      className={`text-[10px] rounded px-1.5 py-0.5 border ${
                        i === 0
                          ? "bg-sky-300/25 border-sky-300/50 text-sky-100 font-medium"
                          : "bg-sky-300/10 border-sky-300/25 text-sky-200/80"
                      }`}
                      title={i === 0 ? "Primary angle (Lumi tunes drafts to this tone)" : "Secondary angle"}
                    >
                      {i === 0 ? "★ " : "🎯 "}{a}
                    </span>
                  ))}
                  {Number.isFinite(c.send_kids_count) && c.send_kids_count > 0 && (
                    <span className="text-[10px] bg-amber-300/20 border border-amber-300/40 text-amber-100 rounded px-1.5 py-0.5 font-medium" title="SEND kids on roll">
                      🎯 {c.send_kids_count} SEND kids
                    </span>
                  )}
                  {(c.websites || []).map((w, i) => {
                    let host = w;
                    try { host = new URL(w).hostname.replace(/^www\./, ""); } catch { /* keep raw */ }
                    return (
                      <a
                        key={`web-${i}`}
                        href={w}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={w}
                        data-testid={`lumi-row-website-${c.id}-${i}`}
                        className="text-[10px] bg-sky-500/15 border border-sky-400/30 text-sky-200 rounded px-1.5 py-0.5 hover:bg-sky-500/30 hover:border-sky-300 transition-colors"
                      >
                        🔗 {host}
                      </a>
                    );
                  })}
                  {c.research_summary && (
                    <span
                      className="text-[10px] bg-sky-300/15 border border-sky-300/40 text-sky-200 rounded px-1.5 py-0.5"
                      title={c.research_summary.slice(0, 400) + (c.research_summary.length > 400 ? "…" : "")}
                      data-testid={`lumi-row-research-${c.id}`}
                    >
                      🔍 researched
                    </span>
                  )}
                </div>
                {c.notes && (
                  <div className="text-[11px] text-white/40 mt-1 line-clamp-2 italic">📝 {c.notes}</div>
                )}
              </div>
              <button
                onClick={() => setEditing({
                  ...c,
                  list_ids: [...(c.list_ids || [])],
                  email_angles: [...(c.email_angles && c.email_angles.length ? c.email_angles : (c.email_angle ? [c.email_angle] : []))],
                  send_kids_count: c.send_kids_count ?? "",
                  websites: [...(c.websites || [])],
                  research_summary: c.research_summary || "",
                  research_at: c.research_at || null,
                  _origEmail: c.email,  // remember pre-edit email so we can spot a change on save
                })}
                data-testid={`lumi-edit-${c.id}`}
                className="text-xs text-white/50 hover:text-white shrink-0"
                title="Edit list memberships"
              >
                Edit
              </button>
              {isUnsub && (
                <button
                  onClick={() => resubscribe(c)}
                  data-testid={`lumi-resubscribe-${c.id}`}
                  className="text-xs text-emerald-300 hover:text-emerald-200 border border-emerald-300/30 rounded-full px-3 py-1 shrink-0"
                  title="Re-enable broadcasts for this contact"
                >
                  ♻ Resubscribe
                </button>
              )}
              <button onClick={() => remove(c.id)} className="text-white/50 hover:text-white text-xs shrink-0">Remove</button>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="lumi-contact-editor">
            <div>
              <div className="text-xs text-white/50 mb-1">Email address</div>
              <input
                type="email"
                value={editing.email || ""}
                onChange={(e) => setEditing((ed) => ({ ...ed, email: e.target.value }))}
                placeholder="email@example.com"
                data-testid="lumi-edit-email"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-bold focus:outline-none focus:border-white/30"
              />
              {(editing._origEmail || editing.email) !== editing.email && (
                <div className="text-[11px] text-amber-300/80 mt-1">
                  ⚠ Changing email — will save as <code className="text-white/70">{editing.email}</code> (was <code className="text-white/40">{editing._origEmail}</code>).
                </div>
              )}
              <input
                value={editing.name || ""}
                onChange={(e) => setEditing((ed) => ({ ...ed, name: e.target.value }))}
                placeholder="Name (optional)"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
              {/* Org picker (multi) + include-in-broadcast for the editing contact */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-white/50">🏢 Orgs:</span>
                {(() => {
                  const cur = editing.org_ids || (editing.org_id ? [editing.org_id] : []);
                  if (orgs.length === 0) return <span className="text-xs text-white/35 italic">No orgs yet</span>;
                  return orgs.map((o) => {
                    const on = cur.includes(o.id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggleOrgIdEdit(o.id)}
                        data-testid={`lumi-edit-org-pick-${o.id}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all ${on ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/70 hover:text-white"}`}
                      >
                        {on ? "✓ " : ""}{o.name}
                      </button>
                    );
                  });
                })()}
                <button
                  type="button"
                  onClick={() => setOrgEditor({ id: null, name: "", websites: [], notes: "", color: null })}
                  className="text-xs px-2 py-1.5 bg-white/5 border border-dashed border-white/20 rounded-lg hover:bg-white/10 text-white/60 hover:text-white"
                >+ New</button>
                {(() => {
                  const cur = editing.org_ids || (editing.org_id ? [editing.org_id] : []);
                  if (cur.length === 0) return null;
                  return cur.map((oid) => {
                    const org = orgs.find((o) => o.id === oid);
                    if (!org) return null;
                    return (
                      <button
                        key={oid}
                        type="button"
                        onClick={() => setOrgEditor({ ...org })}
                        className="text-[10px] px-2 py-1 bg-violet-300/15 text-violet-100 border border-violet-300/40 rounded-lg hover:bg-violet-300/25"
                        data-testid={`lumi-edit-view-org-${oid}`}
                        title={`Open ${org.name} to edit / research`}
                      >🔬 {org.name} →</button>
                    );
                  });
                })()}
              </div>
              <label className="mt-2 flex items-center gap-1.5 text-xs text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.include_in_broadcast !== false}
                  onChange={(e) => setEditing((ed) => ({ ...ed, include_in_broadcast: e.target.checked }))}
                  data-testid="lumi-edit-include-broadcast"
                  className="accent-emerald-300"
                />
                Include in broadcasts &amp; drips
                {editing.include_in_broadcast === false && <span className="text-amber-300/80 ml-1">(currently excluded — useful for "head only" contacts)</span>}
              </label>
            </div>
            <div>
              <div className="text-xs text-white/50 mb-1.5">Lists (tick all that apply):</div>
              <div className="flex flex-wrap gap-2">
                {lists.map((l) => {
                  const on = (editing.list_ids || []).includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setEditing((ed) => ({ ...ed, list_ids: on ? ed.list_ids.filter((x) => x !== l.id) : [...(ed.list_ids || []), l.id] }))}
                      data-testid={`lumi-edit-list-${l.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                        on
                          ? "bg-emerald-300/20 border-emerald-300/40 text-emerald-100"
                          : "bg-white/5 border-white/15 text-white/60 hover:text-white"
                      }`}
                    >
                      {on ? "✓ " : ""}{l.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-xs text-white/50 mb-1.5">
                Email angles — tick all that apply (<strong>first one</strong> = primary):
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[...angles, ...((editing.email_angles || []).filter((a) => !angles.includes(a)))].map((a) => {
                  const arr = editing.email_angles || [];
                  const idx = arr.indexOf(a);
                  const on = idx >= 0;
                  const isPrimary = idx === 0;
                  const isCustom = !angles.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setEditing((ed) => {
                        const cur = ed.email_angles || [];
                        const next = cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a];
                        return { ...ed, email_angles: next };
                      })}
                      data-testid={`lumi-edit-angle-${a}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                        isPrimary
                          ? "bg-sky-300/30 border-sky-300/60 text-sky-50"
                          : on
                            ? "bg-sky-300/15 border-sky-300/40 text-sky-100"
                            : isCustom
                              ? "bg-amber-300/10 border-amber-300/30 text-amber-100/80 hover:text-amber-50"
                              : "bg-white/5 border-white/15 text-white/60 hover:text-white"
                      }`}
                      title={isCustom ? "Custom angle" : ""}
                    >
                      {isPrimary ? "★ " : on ? "✓ " : ""}{isCustom && !on ? "✎ " : ""}{a}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2">
                <input
                  type="text"
                  placeholder="+ Add a custom angle (press Enter)"
                  data-testid="lumi-edit-custom-angle"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                      const newAngle = e.currentTarget.value.trim();
                      setEditing((ed) => {
                        const cur = ed.email_angles || [];
                        return cur.includes(newAngle) ? ed : { ...ed, email_angles: [...cur, newAngle] };
                      });
                      e.currentTarget.value = "";
                      e.preventDefault();
                    }
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-amber-300/40"
                />
              </div>
              {(editing.email_angles || []).length > 1 && (
                <div className="text-[11px] text-white/40 mt-2">
                  Tip: untick &amp; re-tick to reorder — newly ticked angles are added to the end. Primary is whichever you ticked first that's still on.
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-white/50 mb-1" title="For schools — how many SEND kids on roll. Helps prioritise outreach.">🎯 SEND kids count (schools only — leave blank otherwise)</div>
              <input
                type="number"
                min="0"
                value={editing.send_kids_count ?? ""}
                onChange={(e) => setEditing((ed) => ({ ...ed, send_kids_count: e.target.value }))}
                data-testid="lumi-edit-send-kids"
                placeholder="e.g. 300"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <div className="text-xs text-white/50 mb-1">Notes (private — Lumi reads these)</div>
              <textarea
                value={editing.notes || ""}
                onChange={(e) => setEditing((ed) => ({ ...ed, notes: e.target.value }))}
                rows={4}
                placeholder="Anything Lumi should know about this contact"
                data-testid="lumi-edit-notes"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm resize-y"
              />
            </div>
            <div>
              <div className="text-xs text-white/50 mb-1.5">🔗 Websites (links related to this contact — press Enter to add)</div>
              <WebsiteListEditor
                websites={editing.websites || []}
                onChange={(next) => setEditing((ed) => ({ ...ed, websites: next }))}
                testIdPrefix="lumi-edit"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs text-white/50">🔍 Lumi research</div>
                <button
                  type="button"
                  onClick={researchContact}
                  disabled={researching || !(editing.websites || []).length}
                  data-testid="lumi-edit-research"
                  className="text-xs px-3 py-1 rounded-full bg-sky-300/20 border border-sky-300/40 text-sky-100 hover:bg-sky-300/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title={!(editing.websites || []).length ? "Add a website first" : "Lumi reads the websites and summarises this contact"}
                >
                  {researching ? "Reading websites…" : (editing.research_summary ? "🔄 Refresh" : "🔍 Research with Lumi")}
                </button>
              </div>
              {editing.research_summary ? (
                <div className="bg-sky-300/5 border border-sky-300/20 rounded-lg p-3 space-y-1.5" data-testid="lumi-edit-research-summary">
                  <div className="text-[11px] text-sky-200/70">
                    {editing.research_at ? `Last researched ${new Date(editing.research_at).toLocaleString()}` : "Saved"}
                  </div>
                  <div className="text-xs text-white/85 whitespace-pre-wrap leading-relaxed">{editing.research_summary}</div>
                </div>
              ) : (
                <div className="text-[11px] text-white/40 italic px-1">
                  Click Research and Lumi will visit the websites above, summarise this contact, and use the notes whenever you mention them in chat.
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 border border-white/20 rounded-lg hover:bg-white/10">Cancel</button>
              <button onClick={saveEdit} data-testid="lumi-edit-save" className="text-xs px-3 py-1.5 bg-white text-black rounded-lg font-medium">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Org editor — create new / rename / manage URLs / run org-level Lumi research */}
      {orgEditor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setOrgEditor(null); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="lumi-org-editor">
            <div className="font-bold text-lg">{orgEditor.id ? "🏢 Edit org" : "🏢 New org"}</div>
            <input
              value={orgEditor.name}
              onChange={(e) => setOrgEditor({ ...orgEditor, name: e.target.value })}
              placeholder="School / company / association name"
              data-testid="lumi-org-name"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <div>
              <div className="text-xs text-white/50 mb-1">Websites (Lumi reads these for org research)</div>
              {(orgEditor.websites || []).map((w, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <input
                    value={w}
                    onChange={(e) => {
                      const next = [...orgEditor.websites];
                      next[i] = e.target.value;
                      setOrgEditor({ ...orgEditor, websites: next });
                    }}
                    placeholder="https://..."
                    className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
                  />
                  <button
                    onClick={() => setOrgEditor({ ...orgEditor, websites: orgEditor.websites.filter((_, j) => j !== i) })}
                    className="text-white/30 hover:text-rose-300 px-2"
                  >✕</button>
                </div>
              ))}
              <button
                onClick={() => setOrgEditor({ ...orgEditor, websites: [...(orgEditor.websites || []), ""] })}
                className="text-xs text-white/60 hover:text-white border border-dashed border-white/20 rounded px-3 py-1"
              >+ Add URL</button>
            </div>
            <textarea
              value={orgEditor.notes || ""}
              onChange={(e) => setOrgEditor({ ...orgEditor, notes: e.target.value })}
              placeholder="Notes (only you see these)"
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs"
            />

            {orgEditor.id && (orgEditor.research_summary || orgEditor._showResearch) && (
              <div className="bg-violet-300/10 border border-violet-300/30 rounded-lg p-3">
                <div className="text-xs uppercase tracking-widest text-violet-200 font-bold mb-1">🔬 Lumi org research</div>
                {orgEditor.research_summary ? (
                  <div className="text-xs text-white/80 whitespace-pre-wrap">{orgEditor.research_summary}</div>
                ) : (
                  <div className="text-xs text-white/40 italic">No research yet — click "Research with Lumi" below.</div>
                )}
                {orgEditor.research_at && (
                  <div className="text-[10px] text-white/40 mt-1">Last researched {fmtDateTime(orgEditor.research_at)}</div>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-between items-center pt-2 flex-wrap">
              <div className="flex gap-2">
                {orgEditor.id && (
                  <>
                    <button
                      onClick={() => researchOrg(orgEditor)}
                      disabled={researchingOrgId === orgEditor.id || !(orgEditor.websites || []).filter(Boolean).length}
                      data-testid="lumi-org-research-btn"
                      className="text-xs px-3 py-1.5 bg-violet-300/20 hover:bg-violet-300/30 border border-violet-300/40 text-violet-100 rounded-lg disabled:opacity-50"
                    >
                      {researchingOrgId === orgEditor.id ? "🔬 Researching…" : "🔬 Research with Lumi"}
                    </button>
                    <button
                      onClick={() => deleteOrg(orgEditor.id)}
                      className="text-xs px-3 py-1.5 text-rose-200 hover:bg-rose-300/10 rounded-lg"
                    >Delete</button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setOrgEditor(null)} className="text-xs px-3 py-1.5 border border-white/20 rounded-lg hover:bg-white/10">Cancel</button>
                <button onClick={saveOrg} data-testid="lumi-org-save" className="text-xs px-3 py-1.5 bg-white text-black rounded-lg font-medium">{orgEditor.id ? "Save" : "Create"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListsTab({ lists, refresh }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState("");  // empty = no colour (neutral chip)
  const [editing, setEditing] = useState(null); // {id, name, description, color}

  const create = async () => {
    if (!name) return;
    try {
      await lumiFetch("/lists", { method: "POST", body: JSON.stringify({ name, description: desc, color: color || null }) });
      setName(""); setDesc(""); setColor("");
      toast.success("List created");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not create list"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this list? Contacts are NOT deleted, just removed from this list.")) return;
    await lumiFetch(`/lists/${id}`, { method: "DELETE" });
    refresh();
  };
  const saveEdit = async () => {
    if (!editing.name?.trim()) return toast.error("Name required");
    try {
      await lumiFetch(`/lists/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify({ name: editing.name, description: editing.description || "", color: editing.color || null }),
      });
      toast.success("List updated");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not update"); }
  };
  const downloadListCsv = async (l) => {
    try {
      // Use lumiFetch directly to keep auth headers; receive blob via the
      // wrapper's underlying fetch path. Here we go straight to fetch since
      // lumiFetch json-parses, and we need raw bytes for the CSV download.
      const token = localStorage.getItem("lumi_session_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lumi/lists/${l.id}/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) {
        const errText = await r.text().catch(() => "");
        throw new Error(errText || `Export failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (l.name || "list").replace(/[^a-z0-9._-]+/gi, "_");
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `lumi-${safe}-${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded "${l.name}" as CSV`);
    } catch (e) {
      toast.error(e.message || "Could not download CSV");
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full">
      <h2 className="text-2xl font-bold mb-4">Lists (audiences)</h2>
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4 space-y-2">
        <div className="grid gap-2 sm:grid-cols-[1fr,1.5fr,auto]">
          <input data-testid="lumi-list-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="List name" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="lumi-list-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="lumi-list-create" onClick={create} className="bg-white text-black rounded-lg px-4 py-2 text-sm font-medium">Create</button>
        </div>
        <ListColorPicker color={color} onChange={setColor} testIdPrefix="lumi-list-create" />
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
        {lists.length === 0 && <div className="p-6 text-center text-white/40 text-sm">No lists yet — create one to broadcast.</div>}
        {lists.map((l) => (
          <div key={l.id} className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`lumi-list-row-${l.id}`}>
            <span
              className="w-3 h-3 rounded-full shrink-0 border border-white/15"
              style={{ background: l.color || "rgba(255,255,255,0.1)" }}
              title={l.color ? `Colour: ${l.color}` : "No colour"}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{l.name}</div>
              <div className="text-xs text-white/50 truncate">{l.description || "—"} · {l.contact_count} contacts</div>
            </div>
            <button
              onClick={() => setEditing({ id: l.id, name: l.name, description: l.description || "", color: l.color || "" })}
              data-testid={`lumi-list-edit-${l.id}`}
              className="text-white/50 hover:text-white text-xs shrink-0"
            >
              Edit
            </button>
            <button
              onClick={() => downloadListCsv(l)}
              data-testid={`lumi-list-export-${l.id}`}
              className="text-white/50 hover:text-emerald-300 text-xs shrink-0"
              title="Download all contacts in this list as CSV"
            >
              ⬇ CSV
            </button>
            <button onClick={() => remove(l.id)} className="text-white/50 hover:text-white text-xs shrink-0">Delete</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="lumi-list-editor">
            <div className="font-bold">Edit list</div>
            <input
              value={editing.name}
              onChange={(e) => setEditing((ed) => ({ ...ed, name: e.target.value }))}
              placeholder="List name"
              data-testid="lumi-list-edit-name"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <input
              value={editing.description || ""}
              onChange={(e) => setEditing((ed) => ({ ...ed, description: e.target.value }))}
              placeholder="Description (optional)"
              data-testid="lumi-list-edit-desc"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <ListColorPicker
              color={editing.color || ""}
              onChange={(c) => setEditing((ed) => ({ ...ed, color: c }))}
              testIdPrefix="lumi-list-edit"
            />
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 border border-white/20 rounded-lg hover:bg-white/10">Cancel</button>
              <button onClick={saveEdit} data-testid="lumi-list-edit-save" className="text-xs px-3 py-1.5 bg-white text-black rounded-lg font-medium">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImagesTab({ images, refresh, onPickAsFooter }) {
  const totalSize = images.reduce((sum, im) => sum + (im.size || 0), 0);
  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  const remove = async (im) => {
    if (!window.confirm(`Delete "${im.filename}"?\n\nIf this image is used in any saved draft or sent email, it will become a broken image.`)) return;
    try {
      await lumiFetch(`/images/${im.id}`, { method: "DELETE" });
      toast.success("Image deleted");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not delete"); }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-6xl mx-auto w-full" data-testid="lumi-images-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold">Image library</h2>
          <p className="text-xs text-white/50 mt-1">{images.length} image{images.length === 1 ? "" : "s"} · {fmtSize(totalSize)} total · uploaded images can be reused across emails.</p>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-10 text-center text-white/40 text-sm">
          No images yet. Upload one from the Compose tab → Seasonal image section.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((im) => (
            <div key={im.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden flex flex-col" data-testid={`lumi-image-${im.id}`}>
              <div className="aspect-square bg-black/30 flex items-center justify-center overflow-hidden">
                <img src={im.url} alt={im.filename} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="text-xs text-white/80 truncate" title={im.filename}>{im.filename}</div>
                <div className="text-[10px] text-white/40 flex justify-between">
                  <span>{fmtSize(im.size || 0)}</span>
                  <span>{im.created_at ? fmtDateTime(im.created_at) : ""}</span>
                </div>
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => onPickAsFooter(im.url)}
                    data-testid={`lumi-image-use-${im.id}`}
                    className="flex-1 text-[11px] bg-white/10 hover:bg-white/20 rounded-md py-1.5"
                    title="Use as seasonal image in current draft"
                  >
                    Use
                  </button>
                  <button
                    onClick={() => copyUrl(im.url)}
                    className="text-[11px] bg-white/5 hover:bg-white/10 rounded-md px-2 py-1.5"
                    title="Copy URL"
                  >
                    🔗
                  </button>
                  <button
                    onClick={() => remove(im)}
                    data-testid={`lumi-image-delete-${im.id}`}
                    className="text-[11px] bg-white/5 hover:bg-red-500/20 hover:text-red-300 rounded-md px-2 py-1.5"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ sends, scheduled = [], drafts, onLoadDraft, refresh }) {
  const testCount = sends.filter((s) => s.type === "test").length;
  // Sort each scheduled bucket so the LATEST date/time is on top — saves Louise scrolling.
  // For pending we sort by upcoming-soonest (small future date first); for past
  // (already fired) we sort by most-recently-fired first.
  const _ts = (s) => Date.parse(s.fired_at || s.scheduled_at || s.created_at || 0) || 0;
  const pendingScheduled = scheduled.filter((s) => s.status === "pending").sort((a, b) => _ts(a) - _ts(b));
  const pausedScheduled = scheduled.filter((s) => s.status === "paused").sort((a, b) => _ts(b) - _ts(a));
  const pastScheduled = scheduled.filter((s) => !["pending", "paused"].includes(s.status)).sort((a, b) => _ts(b) - _ts(a));
  const [editingSched, setEditingSched] = useState(null);

  // Date-range filter for past activity. "today" / "7d" / "30d" / "all".
  // Defaults to last 7 days — keeps the page short on a busy History tab while
  // still showing this week's sends at a glance.
  const [pastRange, setPastRange] = useState("7d");
  // Which scheduled-card sub-section to expand. Pending defaults to open,
  // paused + past stay collapsed until clicked — keeps the page short.
  const [openSchedCard, setOpenSchedCard] = useState("pending");

  const cancelScheduled = async (sch) => {
    if (!window.confirm(`Cancel the scheduled broadcast of "${sch.draft_subject}" to ${sch.list_name}?`)) return;
    try {
      await lumiFetch(`/sends/scheduled/${sch.id}/cancel`, { method: "POST" });
      toast.success("Scheduled broadcast cancelled");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not cancel"); }
  };

  const pauseScheduled = async (sch) => {
    try {
      await lumiFetch(`/sends/scheduled/${sch.id}/pause`, { method: "POST" });
      toast.success("Paused — won't fire at the scheduled time");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not pause"); }
  };

  const resumeScheduled = async (sch) => {
    try {
      await lumiFetch(`/sends/scheduled/${sch.id}/resume`, { method: "POST" });
      toast.success("Resumed");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not resume — try editing the time first"); }
  };

  const saveEditSched = async () => {
    const dt = new Date(editingSched._scheduledAt);
    if (isNaN(dt.getTime())) return toast.error("Pick a valid date and time");
    if (dt <= new Date()) return toast.error("Pick a future time");
    try {
      await lumiFetch(`/sends/scheduled/${editingSched.id}`, {
        method: "PUT",
        body: JSON.stringify({
          scheduled_at: dt.toISOString(),
          personalise_by_angle: editingSched.personalise_by_angle,
          note: editingSched.note || "",
        }),
      });
      toast.success("Schedule updated");
      setEditingSched(null);
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not update"); }
  };

  const deleteScheduled = async (sch) => {
    if (!window.confirm(`Permanently delete this ${sch.status} record?`)) return;
    try {
      await lumiFetch(`/sends/scheduled/${sch.id}`, { method: "DELETE" });
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not delete"); }
  };

  const renderSchedRow = (sch, kind) => (
    <div key={sch.id} className="px-4 py-3 text-sm flex items-start gap-3" data-testid={`lumi-scheduled-${sch.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
            kind === "pending" ? "bg-sky-300/20 text-sky-200"
            : kind === "paused" ? "bg-amber-300/20 text-amber-200"
            : sch.status === "fired" ? "bg-emerald-300/20 text-emerald-200"
            : sch.status === "cancelled" ? "bg-white/15 text-white/60"
            : "bg-rose-300/20 text-rose-200"
          }`}>{sch.status}</span>
          <span className="font-medium truncate">{sch.draft_subject}</span>
          {sch.personalise_by_angle && <span className="text-[10px] text-emerald-200">✨ personalised</span>}
        </div>
        <div className="text-xs text-white/50 mt-1">
          → {sch.list_name} · fires {fmtDateTime(sch.scheduled_at)}
        </div>
        {sch.note && <div className="text-xs text-white/40 mt-0.5 italic">📝 {sch.note}</div>}
        {sch.error && <div className="text-xs text-rose-300 mt-0.5">{sch.error}</div>}
      </div>
      <div className="flex flex-col gap-1 shrink-0 text-xs">
        {kind === "pending" && (
          <>
            <button onClick={() => setEditingSched({ ...sch, _scheduledAt: toLocalInputValue(sch.scheduled_at) })} data-testid={`lumi-edit-scheduled-${sch.id}`} className="text-white/70 hover:text-white border border-white/20 rounded-full px-3 py-1">Edit</button>
            <button onClick={() => pauseScheduled(sch)} data-testid={`lumi-pause-scheduled-${sch.id}`} className="text-amber-300 hover:text-amber-200 border border-amber-300/30 rounded-full px-3 py-1">⏸ Pause</button>
            <button onClick={() => cancelScheduled(sch)} data-testid={`lumi-cancel-scheduled-${sch.id}`} className="text-white/60 hover:text-red-300 border border-white/20 rounded-full px-3 py-1">Cancel</button>
          </>
        )}
        {kind === "paused" && (
          <>
            <button onClick={() => setEditingSched({ ...sch, _scheduledAt: toLocalInputValue(sch.scheduled_at) })} data-testid={`lumi-edit-scheduled-${sch.id}`} className="text-white/70 hover:text-white border border-white/20 rounded-full px-3 py-1">Edit</button>
            <button onClick={() => resumeScheduled(sch)} data-testid={`lumi-resume-scheduled-${sch.id}`} className="text-emerald-300 hover:text-emerald-200 border border-emerald-300/30 rounded-full px-3 py-1">▶ Resume</button>
            <button onClick={() => cancelScheduled(sch)} data-testid={`lumi-cancel-scheduled-${sch.id}`} className="text-white/60 hover:text-red-300 border border-white/20 rounded-full px-3 py-1">Cancel</button>
          </>
        )}
        {kind === "past" && (
          <button onClick={() => deleteScheduled(sch)} className="text-white/40 hover:text-red-300">✕</button>
        )}
      </div>
    </div>
  );

  const deleteOne = async (s) => {
    if (s.type === "broadcast") {
      toast.error("Broadcast records can't be deleted (audit trail)");
      return;
    }
    const label = s.type === "inbox_reply" ? "direct-reply record" : "test record";
    if (!window.confirm(`Delete ${label} for "${s.subject}"? (the email itself is unaffected)`)) return;
    try {
      await lumiFetch(`/sends/${s.id}`, { method: "DELETE" });
      toast.success("Record deleted");
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not delete"); }
  };

  const clearAllTests = async () => {
    if (!window.confirm(`Delete all ${testCount} test records? Broadcasts are never touched.`)) return;
    try {
      const r = await lumiFetch(`/sends/clear-tests`, { method: "POST" });
      toast.success(`Cleared ${r.deleted} test records`);
      refresh();
    } catch (e) { toast.error(e?.data?.detail || "Could not clear"); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full grid lg:grid-cols-2 gap-6">
      <div>
        <h2 className="text-xl font-bold mb-3">Saved drafts</h2>
        <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
          {drafts.length === 0 && <div className="p-5 text-center text-white/40 text-sm">No drafts yet.</div>}
          {drafts.map((d) => (
            <div key={d.id} className="flex items-stretch hover:bg-white/5">
              <button
                onClick={() => onLoadDraft(d.id)}
                data-testid={`lumi-draft-${d.id}`}
                className="flex-1 text-left px-4 py-3 text-sm min-w-0"
              >
                <div className="font-medium truncate">{d.subject || d.title || "(no subject)"}</div>
                <div className="text-xs text-white/40 flex items-center gap-2 flex-wrap mt-0.5">
                  <span>Last edited {fmtDateTime(d.updated_at)}</span>
                  {(() => {
                    const s = d.stats || {};
                    const sent = (s.broadcast_count || 0) + (s.test_count || 0);
                    if (!sent) return null;
                    return (
                      <span
                        className="inline-flex items-center gap-1 bg-emerald-300/15 border border-emerald-300/40 text-emerald-200 rounded-full px-2 py-[1px] text-[10px] font-medium"
                        title={`${s.broadcast_count || 0} broadcast(s), ${s.test_count || 0} test(s)${s.last_sent_at ? ` · last ${fmtDateTime(s.last_sent_at)}` : ""}`}
                        data-testid={`lumi-draft-stats-${d.id}`}
                      >
                        ✉️ {sent}× {s.broadcast_count > 0 ? "sent" : "tested"}
                        {s.last_sent_at && <span className="opacity-75">· {fmtDateTime(s.last_sent_at)}</span>}
                      </span>
                    );
                  })()}
                </div>
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const copy = await lumiFetch(`/drafts/${d.id}/duplicate`, { method: "POST" });
                    toast.success(`Duplicated as "${copy.title}"`);
                    refresh();
                    onLoadDraft(copy.id);
                  } catch (err) { toast.error(err?.data?.detail || "Could not duplicate"); }
                }}
                data-testid={`lumi-draft-duplicate-${d.id}`}
                title="Duplicate as template"
                className="px-3 text-xs text-white/40 hover:text-white border-l border-white/10"
              >
                ⎘
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Delete draft "${d.title || d.subject || '(no subject)'}"? This cannot be undone.`)) return;
                  try {
                    await lumiFetch(`/drafts/${d.id}`, { method: "DELETE" });
                    toast.success("Draft deleted");
                    refresh();
                  } catch (err) { toast.error(err?.data?.detail || "Could not delete"); }
                }}
                data-testid={`lumi-draft-delete-${d.id}`}
                title="Delete draft permanently"
                className="px-3 text-xs text-white/40 hover:text-red-300 border-l border-white/10"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold">Send history</h2>
          {testCount > 0 && (
            <button
              onClick={clearAllTests}
              data-testid="lumi-clear-tests"
              className="text-xs text-white/60 hover:text-red-300 border border-white/20 hover:border-red-400/40 rounded-full px-3 py-1"
            >
              🗑️ Clear all tests ({testCount})
            </button>
          )}
        </div>

        {/* Scheduled broadcasts — split into 3 distinct cards (Pending / Paused / Past).
            Pending is the most actionable so it's expanded by default; the others
            collapse to one-line summaries to keep the page short. */}
        {(pendingScheduled.length > 0 || pausedScheduled.length > 0 || pastScheduled.length > 0) && (
          <div className="mb-4 space-y-2" data-testid="lumi-scheduled-list">
            <h3 className="text-xs uppercase tracking-wider text-white/40">⏰ Scheduled broadcasts</h3>

            {/* Pending — actionable, default open */}
            {pendingScheduled.length > 0 && (
              <div className="bg-sky-300/5 border border-sky-300/20 rounded-xl overflow-hidden" data-testid="lumi-sched-card-pending">
                <button
                  onClick={() => setOpenSchedCard(openSchedCard === "pending" ? null : "pending")}
                  className="w-full px-4 py-2.5 flex items-center justify-between text-sm hover:bg-sky-300/5"
                  data-testid="lumi-sched-toggle-pending"
                >
                  <span className="font-semibold text-sky-200">⏰ Pending ({pendingScheduled.length})</span>
                  <span className="text-xs text-white/40">{openSchedCard === "pending" ? "Hide ▾" : "Show ▸"}</span>
                </button>
                {openSchedCard === "pending" && (
                  <div className="divide-y divide-white/10 border-t border-white/10">
                    {pendingScheduled.map((sch) => renderSchedRow(sch, "pending"))}
                  </div>
                )}
              </div>
            )}

            {/* Paused — collapsed by default */}
            {pausedScheduled.length > 0 && (
              <div className="bg-amber-300/5 border border-amber-300/20 rounded-xl overflow-hidden" data-testid="lumi-sched-card-paused">
                <button
                  onClick={() => setOpenSchedCard(openSchedCard === "paused" ? null : "paused")}
                  className="w-full px-4 py-2.5 flex items-center justify-between text-sm hover:bg-amber-300/5"
                  data-testid="lumi-sched-toggle-paused"
                >
                  <span className="font-semibold text-amber-200">⏸️ Paused ({pausedScheduled.length})</span>
                  <span className="text-xs text-white/40">{openSchedCard === "paused" ? "Hide ▾" : "Show ▸"}</span>
                </button>
                {openSchedCard === "paused" && (
                  <div className="divide-y divide-white/10 border-t border-white/10">
                    {pausedScheduled.map((sch) => renderSchedRow(sch, "paused"))}
                  </div>
                )}
              </div>
            )}

            {/* Past — already fired/cancelled/failed, collapsed by default */}
            {pastScheduled.length > 0 && (
              <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden" data-testid="lumi-sched-card-past">
                <button
                  onClick={() => setOpenSchedCard(openSchedCard === "past" ? null : "past")}
                  className="w-full px-4 py-2.5 flex items-center justify-between text-sm hover:bg-white/5"
                  data-testid="lumi-sched-toggle-past"
                >
                  <span className="font-semibold text-white/70">📂 Past ({pastScheduled.length})</span>
                  <span className="text-xs text-white/40">{openSchedCard === "past" ? "Hide ▾" : "Show ▸"}</span>
                </button>
                {openSchedCard === "past" && (
                  <div className="divide-y divide-white/10 border-t border-white/10">
                    {pastScheduled.map((sch) => renderSchedRow(sch, "past"))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Date-range filter for the past sends list (Today / 7d / 30d / All) */}
        <div className="flex items-center gap-2 mb-3 text-xs" data-testid="lumi-date-filter">
          <span className="text-white/40 uppercase tracking-wider">Show:</span>
          {[
            { id: "today", label: "Today" },
            { id: "7d", label: "Last 7 days" },
            { id: "30d", label: "Last 30 days" },
            { id: "all", label: "All time" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setPastRange(opt.id)}
              data-testid={`lumi-date-filter-${opt.id}`}
              className={`px-3 py-1 rounded-full transition-all ${
                pastRange === opt.id
                  ? "bg-white text-black"
                  : "bg-white/5 text-white/60 hover:text-white hover:bg-white/10"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Edit-schedule modal */}
        {editingSched && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setEditingSched(null); }}>
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="lumi-edit-schedule-modal">
              <div className="font-bold">Edit scheduled broadcast</div>
              <div className="text-xs text-white/50">"{editingSched.draft_subject}" → {editingSched.list_name}</div>
              <div>
                <label className="text-xs text-white/50 block mb-1">When (your local time)</label>
                <input
                  type="datetime-local"
                  value={editingSched._scheduledAt}
                  onChange={(e) => setEditingSched((s) => ({ ...s, _scheduledAt: e.target.value }))}
                  data-testid="lumi-edit-sched-at"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1">Note</label>
                <input
                  type="text"
                  value={editingSched.note || ""}
                  onChange={(e) => setEditingSched((s) => ({ ...s, note: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={!!editingSched.personalise_by_angle} onChange={(e) => setEditingSched((s) => ({ ...s, personalise_by_angle: e.target.checked }))} className="accent-emerald-300" />
                ✨ Personalise by recipient angle
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setEditingSched(null)} className="text-xs px-3 py-1.5 border border-white/20 rounded-lg hover:bg-white/10">Cancel</button>
                <button onClick={saveEditSched} data-testid="lumi-edit-sched-save" className="text-xs px-3 py-1.5 bg-white text-black rounded-lg font-medium">Save</button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
          {(() => {
            // Apply the Today / 7d / 30d / All filter to the send list. We
            // compare `at` (the send timestamp) to a cutoff. "all" disables it.
            const now = Date.now();
            const cutoff = {
              today: now - 24 * 3600 * 1000,
              "7d":   now - 7  * 24 * 3600 * 1000,
              "30d":  now - 30 * 24 * 3600 * 1000,
              all:    0,
            }[pastRange] ?? 0;
            const filtered = sends.filter((s) => (Date.parse(s.at || s.created_at) || 0) >= cutoff);
            if (filtered.length === 0) {
              return (
                <div className="p-5 text-center text-white/40 text-sm">
                  {sends.length === 0
                    ? "Nothing sent yet."
                    : `No sends in ${pastRange === "today" ? "today" : pastRange === "7d" ? "the last 7 days" : "the last 30 days"}.`}
                </div>
              );
            }
            return filtered.map((s) => {
            const isTest = s.type === "test";
            const isReply = s.type === "inbox_reply";
            const isBroadcast = s.type === "broadcast";
            const badgeClass = isTest
              ? "bg-amber-200/20 text-amber-200"
              : isReply
                ? "bg-sky-300/20 text-sky-200"
                : "bg-emerald-300/20 text-emerald-200";
            const badgeLabel = isReply ? "direct reply" : s.type;
            const subline = isTest
              ? `to ${(s.to || []).join(", ")}`
              : isReply
                ? `to ${(s.to || []).join(", ")}${s.from_name ? ` (${s.from_name})` : ""}`
                : `${s.sent_count} sent · ${s.failed_count} failed`;
            return (
              <div key={s.id} className="px-4 py-3 text-sm flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${badgeClass}`} data-testid={`lumi-history-badge-${s.id}`}>{badgeLabel}</span>
                    <span className="font-medium truncate flex-1">{s.subject}</span>
                  </div>
                  <div className="text-xs text-white/40 mt-1 truncate">
                    {subline} · {fmtDateTime(s.at)}
                  </div>
                </div>
                {(isTest || isReply) && (
                  <button
                    onClick={() => deleteOne(s)}
                    data-testid={`lumi-delete-send-${s.id}`}
                    className="text-white/40 hover:text-red-300 text-xs shrink-0"
                    title={isReply ? "Delete this reply record (the email is already sent)" : "Delete this test record"}
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          });
          })()}
        </div>
      </div>
    </div>
  );
}


function SnippetsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, label, body, kind} or "new-text" / "new-video"
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const [filter, setFilter] = useState("text");  // 'text' | 'video'

  const load = async () => {
    setLoading(true);
    try { setItems(await lumiFetch("/snippets")); }
    catch (e) { toast.error(e?.data?.detail || "Could not load snippets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = (kind) => { setEditing({ kind }); setLabel(""); setBody(""); };
  const startEdit = (s) => { setEditing(s); setLabel(s.label); setBody(s.body); };
  const cancel = () => { setEditing(null); setLabel(""); setBody(""); };

  const isVideoEditor = editing?.kind === "video";

  const save = async () => {
    if (!label.trim() || !body.trim()) return toast.error("Label and body are both required");
    if (isVideoEditor && !/^https?:\/\//i.test(body.trim())) return toast.error("Video snippet body must be a URL (https://...)");
    const payload = { label, body, kind: editing.kind || "text" };
    try {
      if (!editing.id) {
        await lumiFetch("/snippets", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Snippet saved ✨");
      } else {
        await lumiFetch(`/snippets/${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
        toast.success("Snippet updated");
      }
      cancel();
      load();
    } catch (e) { toast.error(e?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this snippet?")) return;
    try {
      await lumiFetch(`/snippets/${id}`, { method: "DELETE" });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.data?.detail || "Could not delete"); }
  };

  const visible = items.filter((s) => (s.kind || "text") === filter);
  const textCount = items.filter((s) => (s.kind || "text") === "text").length;
  const videoCount = items.filter((s) => s.kind === "video").length;

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full" data-testid="lumi-snippets-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold">Snippets library</h2>
          <p className="text-xs text-white/50 mt-1">
            Save reusable text phrases and video links. Insert them into chat or emails in one click.
          </p>
        </div>
        {!editing && (
          <div className="flex gap-2">
            <button onClick={() => startNew("text")} data-testid="lumi-snippet-new-text" className="text-sm px-3 py-1.5 bg-white text-black rounded-lg font-medium hover:bg-white/90">+ Text snippet</button>
            <button onClick={() => startNew("video")} data-testid="lumi-snippet-new-video" className="text-sm px-3 py-1.5 bg-amber-200 text-black rounded-lg font-medium hover:bg-amber-100">+ Video link</button>
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter("text")}
          data-testid="lumi-snippet-filter-text"
          className={`text-xs px-3 py-1.5 rounded-full border ${filter === "text" ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:text-white"}`}
        >
          ✂️ Text ({textCount})
        </button>
        <button
          onClick={() => setFilter("video")}
          data-testid="lumi-snippet-filter-video"
          className={`text-xs px-3 py-1.5 rounded-full border ${filter === "video" ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:text-white"}`}
        >
          📺 Video ({videoCount})
        </button>
      </div>

      {editing && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4 space-y-3" data-testid="lumi-snippet-editor">
          <div className="text-xs text-white/50">{isVideoEditor ? "📺 Video link snippet" : "✂️ Text snippet"}</div>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={isVideoEditor ? 'Label (e.g. "Behind the scenes video", "Lullaby for SEND kids")' : 'Label (e.g. "Apology", "Refund offered")'}
            data-testid="lumi-snippet-label"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
          />
          {isVideoEditor ? (
            <input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              data-testid="lumi-snippet-body"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
            />
          ) : (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="The text that gets inserted at your cursor…"
              data-testid="lumi-snippet-body"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:border-white/30"
            />
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={cancel} className="text-xs px-3 py-1.5 border border-white/20 rounded-lg hover:bg-white/10">Cancel</button>
            <button onClick={save} data-testid="lumi-snippet-save" className="text-xs px-3 py-1.5 bg-white text-black rounded-lg font-medium hover:bg-white/90">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
        {loading ? (
          <div className="p-5 text-center text-white/40 text-sm">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            {filter === "text"
              ? <>No text snippets yet. Create one — try labels like <span className="text-white/60">"Where to find Poodle Talk"</span> or <span className="text-white/60">"Refund process"</span>.</>
              : <>No video links yet. Save the YouTube videos you most often link to (e.g. <span className="text-white/60">"Behind the scenes"</span>, <span className="text-white/60">"Calm bedtime song"</span>) and reuse them in any email with one click.</>
            }
          </div>
        ) : (
          visible.map((s) => {
            const m = s.kind === "video" ? (s.body || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/) : null;
            const ytId = m ? m[1] : "";
            return (
              <div key={s.id} className="p-4 flex items-start gap-3" data-testid={`lumi-snippet-row-${s.id}`}>
                {s.kind === "video" && ytId && <img src={`https://img.youtube.com/vi/${ytId}/default.jpg`} alt="" className="w-20 h-14 rounded object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{s.label}</div>
                  <div className="text-xs text-white/50 mt-1 whitespace-pre-wrap line-clamp-3">{s.body}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(s)} className="text-xs px-2 py-1 border border-white/20 rounded-md hover:bg-white/10">Edit</button>
                  <button onClick={() => remove(s.id)} data-testid={`lumi-snippet-delete-${s.id}`} className="text-xs px-2 py-1 text-white/40 hover:text-red-300">✕</button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}



function PurchasesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = async (search = "") => {
    setLoading(true);
    setError("");
    try {
      const qs = search.trim() ? `?q=${encodeURIComponent(search.trim())}` : "";
      const r = await lumiFetch(`/purchases${qs}`);
      setItems(r.items || []);
    } catch (e) {
      setError(e?.data?.detail || "Could not load purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  // Debounce search input → re-fetch with q
  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };

  const resendCode = async (p) => {
    if (!p.customer_email || !p.code_assigned) {
      toast.error("Missing email or code on this purchase");
      return;
    }
    if (!window.confirm(`Re-send the code email\n\n  Code: ${p.code_assigned}\n  Pack: ${p.pack_name || ""}\n  → ${p.customer_email}\n\nThe customer will receive the same branded email Poodle Talk sent originally. Continue?`)) return;
    try {
      const r = await lumiFetch(`/purchases/${encodeURIComponent(p.session_id)}/resend`, { method: "POST" });
      toast.success(`Code email re-sent to ${r.to} 📧`);
    } catch (e) {
      toast.error(e?.data?.detail || "Couldn't resend the code email");
    }
  };

  const fmtAmount = (amount, currency) => {
    if (amount === undefined || amount === null) return "";
    // Stripe stores amounts in major units already in our payment_transactions; show with currency symbol
    const symbol = (currency || "").toUpperCase() === "GBP" ? "£" : (currency || "").toUpperCase() === "USD" ? "$" : (currency ? `${currency.toUpperCase()} ` : "");
    return `${symbol}${Number(amount).toFixed(2)}`;
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-6xl mx-auto w-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Purchases</h2>
          <p className="text-xs text-white/40 mt-1">Every completed Poodle Talk credit purchase. Read-only — search by email to find a customer's code.</p>
        </div>
        <div className="text-xs text-white/40">{loading ? "Loading…" : `${items.length} record${items.length === 1 ? "" : "s"}`}</div>
      </div>

      {/* 🔗 Relink Orphaned Credits — only for rare "cleared their storage" cases */}
      <RelinkCreditsPanel />

      <div className="mb-4">
        <input
          type="search"
          placeholder="🔍 Search by email (e.g. mary@…)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="lumi-purchases-search"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-white/30"
        />
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-400/30 text-red-200 rounded-lg p-3 text-sm mb-4">{error}</div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-white/40 text-sm">
            {q ? `No purchases match "${q}"` : "No purchases yet."}
          </div>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Customer email</th>
                  <th className="text-left px-4 py-3">Pack</th>
                  <th className="text-left px-4 py-3">Code sent</th>
                  <th className="text-left px-4 py-3">Redeemed?</th>
                  <th className="text-left px-4 py-3">Paid</th>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {items.map((p) => (
                  <tr key={p.session_id} data-testid={`lumi-purchase-row-${p.session_id}`}>
                    <td className="px-4 py-3 align-top">
                      <span className="text-white/90 break-all">{p.customer_email || <span className="text-white/30">(unknown)</span>}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-white/70">
                      {p.pack_name || <span className="text-white/30">—</span>}
                      {p.credits ? <div className="text-xs text-white/40">{p.credits} credits</div> : null}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {p.code_assigned ? (
                        <button
                          onClick={() => copyCode(p.code_assigned)}
                          data-testid={`lumi-purchase-copy-${p.session_id}`}
                          className="font-mono text-xs px-2 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 hover:border-white/30 transition"
                          title="Click to copy"
                        >
                          {p.code_assigned} 📋
                        </button>
                      ) : (
                        <span className="text-white/30 text-xs">— not yet —</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {p.redeemed_at ? (
                        <span className="text-emerald-300 text-xs">
                          ✓ {fmtDateTimeShort ? fmtDateTimeShort(p.redeemed_at) : fmtDateTime(p.redeemed_at)}
                        </span>
                      ) : (
                        <span className="text-amber-300/70 text-xs">⏳ pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-white/70">
                      {fmtAmount(p.amount, p.currency)}
                    </td>
                    <td className="px-4 py-3 align-top text-white/60 text-xs">
                      {fmtDateTime(p.completed_at)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <button
                        onClick={() => resendCode(p)}
                        disabled={!p.customer_email || !p.code_assigned}
                        data-testid={`lumi-purchase-resend-${p.session_id}`}
                        className="text-xs px-3 py-1.5 border border-white/20 rounded-md hover:bg-white/10 hover:border-white/40 transition disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
                        title="Re-send the original branded code email to this customer"
                      >
                        📧 Resend
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



// Reusable editor for an array of website URLs. Adds protocol if missing.
function WebsiteListEditor({ websites = [], onChange, testIdPrefix = "lumi-websites" }) {
  const [draft, setDraft] = useState("");

  const normalise = (raw) => {
    let v = raw.trim();
    if (!v) return "";
    // Auto-prepend https:// if scheme missing — friendlier for paste-from-address-bar
    if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
    return v;
  };

  const tryShort = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
  };

  const addOne = (raw) => {
    const v = normalise(raw);
    if (!v) return;
    if (websites.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...(websites || []), v]);
    setDraft("");
  };

  const remove = (idx) => {
    const next = [...(websites || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div>
      {websites.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {websites.map((w, i) => (
            <span
              key={`${w}-${i}`}
              className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/15 rounded-full pl-2 pr-1 py-0.5 text-white/80"
            >
              <a
                href={w}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-300 hover:underline"
                title={w}
                data-testid={`${testIdPrefix}-website-link-${i}`}
              >
                🔗 {tryShort(w)}
              </a>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-white/40 hover:text-red-300 ml-1 px-1"
                title="Remove this website"
                data-testid={`${testIdPrefix}-website-remove-${i}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); addOne(draft); }
        }}
        onBlur={() => draft.trim() && addOne(draft)}
        placeholder="https://example.com — press Enter to add another"
        data-testid={`${testIdPrefix}-website-input`}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-sky-300/40"
      />
    </div>
  );
}


// Reusable colour picker for list (audience) tags. Soft kid-friendly palette
// + free hex input for unlimited customisation.
const LIST_COLOR_PRESETS = [
  { id: "red",     hex: "#E57373", label: "Red" },
  { id: "orange",  hex: "#FFB347", label: "Orange" },
  { id: "amber",   hex: "#F5C064", label: "Amber" },
  { id: "yellow",  hex: "#F1E07C", label: "Yellow" },
  { id: "lime",    hex: "#B5D88A", label: "Lime" },
  { id: "green",   hex: "#7FC79C", label: "Green" },
  { id: "teal",    hex: "#73C7C2", label: "Teal" },
  { id: "sky",     hex: "#7BB6E8", label: "Sky" },
  { id: "blue",    hex: "#6E8FE0", label: "Blue" },
  { id: "indigo",  hex: "#9078D8", label: "Indigo" },
  { id: "violet",  hex: "#B07AC9", label: "Violet" },
  { id: "pink",    hex: "#E48BB6", label: "Pink" },
  { id: "rose",    hex: "#E47C8A", label: "Rose" },
  { id: "brown",   hex: "#B08968", label: "Brown" },
  { id: "slate",   hex: "#7E8B9C", label: "Slate" },
];

function ListColorPicker({ color, onChange, testIdPrefix = "lumi-list-color" }) {
  const isValid = !color || /^#[0-9A-Fa-f]{6}$/.test(color);
  return (
    <div>
      <div className="text-xs text-white/50 mb-1.5">🎨 Colour (optional — helps you scan contact rows quickly)</div>
      <div className="flex flex-wrap gap-1.5 items-center">
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={`${testIdPrefix}-none`}
          className={`text-[10px] px-2 py-1 rounded-full border transition-all ${!color ? "bg-white text-black border-white" : "bg-white/5 border-white/20 text-white/60 hover:text-white"}`}
          title="No colour"
        >
          none
        </button>
        {LIST_COLOR_PRESETS.map((c) => {
          const on = color?.toLowerCase() === c.hex.toLowerCase();
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(c.hex)}
              title={c.label}
              data-testid={`${testIdPrefix}-${c.id}`}
              className={`w-7 h-7 rounded-full border transition-all ${on ? "border-white scale-110 shadow-lg" : "border-white/20 hover:scale-105"}`}
              style={{ background: c.hex }}
            />
          );
        })}
        <input
          type="text"
          value={color || ""}
          onChange={(e) => {
            const v = e.target.value.trim();
            if (!v || /^#?[0-9A-Fa-f]{0,6}$/.test(v)) {
              onChange(v.startsWith("#") || !v ? v : `#${v}`);
            }
          }}
          placeholder="#hex"
          data-testid={`${testIdPrefix}-custom`}
          className={`w-24 bg-black/40 border rounded-lg px-2 py-1 text-xs font-mono ${isValid ? "border-white/15" : "border-red-400/60"}`}
          maxLength={7}
        />
        {color && (
          <span
            className="inline-block ml-1 w-5 h-5 rounded border border-white/20"
            style={{ background: isValid ? color : "transparent" }}
          />
        )}
      </div>
    </div>
  );
}

// 🔗 Relink Orphaned Credits — admin tool inside the Purchases tab.
// Used when a family bought credits, then cleared their browser storage so
// the auto-merge on first-sign-in can't find their old credit row. Lookup
// is a safe dry-run; Confirm only stamps user_id (never moves credits).
function RelinkCreditsPanel() {
  const [open, setOpen] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [googleEmail, setGoogleEmail] = useState("");
  const [lookup, setLookup] = useState(null); // server response
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const runLookup = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLookup(null);
    if (!customerEmail.trim() || !googleEmail.trim()) {
      setError("Both emails are required.");
      return;
    }
    setBusy(true);
    try {
      const qs = `?customer_email=${encodeURIComponent(customerEmail.trim())}&google_email=${encodeURIComponent(googleEmail.trim())}`;
      const r = await lumiFetch(`/admin/relink-lookup${qs}`);
      setLookup(r);
    } catch (err) {
      setError(err?.data?.detail || "Lookup failed.");
    } finally {
      setBusy(false);
    }
  };

  const confirmRelink = async () => {
    if (!lookup) return;
    const toLink = (lookup.credit_rows || []).filter(
      (r) => !r.user_id || r.user_id === lookup?.pt_user?.user_id
    );
    if (toLink.length === 0) {
      toast.info("Nothing to relink — these rows are already linked.");
      return;
    }
    const totalCredits = toLink.reduce((s, r) => s + (r.paid_credits || 0), 0);
    if (!window.confirm(
      `Relink ${toLink.length} credit row(s) totalling ${totalCredits} paid credits to ${lookup.google_email}?\n\nThis is safe: only user_id is stamped. Credit balances are unchanged.`
    )) return;

    setBusy(true);
    setError("");
    try {
      const r = await lumiFetch(`/admin/relink-credits`, {
        method: "POST",
        body: JSON.stringify({
          customer_email: customerEmail.trim(),
          google_email: googleEmail.trim(),
          credit_row_ips: toLink.map((row) => row.ip),
        }),
      });
      toast.success(`Linked ${r.rows_modified} row(s) · ${r.total_paid_credits_after} paid credits now on ${lookup.google_email} ✨`);
      // Refresh the lookup so the UI shows the new state.
      await runLookup();
    } catch (err) {
      setError(err?.data?.detail || "Relink failed.");
    } finally {
      setBusy(false);
    }
  };

  const s = lookup?.summary;

  return (
    <div className="mb-4 bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="lumi-relink-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lumi-relink-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors text-left"
      >
        <div>
          <p className="text-sm font-semibold">🔗 Relink orphaned credits to a Google account</p>
          <p className="text-[11px] text-white/40 mt-0.5">Use this when a family says &ldquo;I bought credits but they&rsquo;re gone after I cleared my browser data.&rdquo;</p>
        </div>
        <span className="text-white/40 text-xs">{open ? "▲ Hide" : "▼ Show"}</span>
      </button>

      {open && (
        <div className="border-t border-white/10 p-4 space-y-3">
          <form onSubmit={runLookup} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-white/50 mb-1">Email used at Stripe checkout (purchase email)</label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="louise@example.com"
                data-testid="lumi-relink-customer-email"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] text-white/50 mb-1">Google email the family signs in with</label>
              <input
                type="email"
                value={googleEmail}
                onChange={(e) => setGoogleEmail(e.target.value)}
                placeholder="louise.smith@gmail.com"
                data-testid="lumi-relink-google-email"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                required
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <button
                type="submit"
                disabled={busy}
                data-testid="lumi-relink-find"
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {busy ? "Looking up…" : "🔍 Find purchases & credit rows"}
              </button>
              {lookup && (
                <button
                  type="button"
                  onClick={() => { setLookup(null); setError(""); }}
                  className="px-3 py-2 text-xs text-white/50 hover:text-white"
                >
                  Clear
                </button>
              )}
            </div>
          </form>

          {error && (
            <div className="bg-red-500/10 border border-red-400/30 text-red-200 rounded-lg p-3 text-xs">{error}</div>
          )}

          {lookup && (
            <div className="space-y-3" data-testid="lumi-relink-results">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
                <Stat label="Purchases" value={s.purchases_found} />
                <Stat label="Credit rows" value={s.credit_rows_found} />
                <Stat label="Already linked" value={s.rows_already_linked} good />
                <Stat label="Ready to link" value={s.rows_to_link} highlight={s.rows_to_link > 0} />
                <Stat label="Locked to other user" value={s.rows_linked_to_other_user} warn={s.rows_linked_to_other_user > 0} />
              </div>

              <div className="bg-black/30 border border-white/10 rounded-lg p-3 text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-white/50">Poodle Talk user for <span className="text-white">{lookup.google_email}</span></span>
                  {lookup.pt_user ? (
                    <span className="text-emerald-300 font-mono text-[10px]">{lookup.pt_user.user_id}</span>
                  ) : (
                    <span className="text-amber-300">⚠ Not signed in yet — ask family to sign in once at poodletalk first</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50">Total paid credits in matched rows</span>
                  <span className="font-semibold">{s.total_paid_credits_in_rows}</span>
                </div>
              </div>

              {lookup.credit_rows && lookup.credit_rows.length > 0 && (
                <div className="bg-black/30 border border-white/10 rounded-lg overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="text-white/40 uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">IP</th>
                        <th className="text-left px-3 py-2">Device ID</th>
                        <th className="text-left px-3 py-2">Free used</th>
                        <th className="text-left px-3 py-2">Paid credits</th>
                        <th className="text-left px-3 py-2">Currently linked to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookup.credit_rows.map((row) => (
                        <tr key={row.ip} className="border-t border-white/5">
                          <td className="px-3 py-2 font-mono">{row.ip}</td>
                          <td className="px-3 py-2 font-mono text-white/60">{row.device_id ? row.device_id.slice(0, 12) + "…" : "—"}</td>
                          <td className="px-3 py-2">{row.free_used ?? 0}</td>
                          <td className="px-3 py-2 font-semibold">{row.paid_credits ?? 0}</td>
                          <td className="px-3 py-2">
                            {row.user_id === lookup?.pt_user?.user_id ? (
                              <span className="text-emerald-300">✓ This family</span>
                            ) : row.user_id ? (
                              <span className="text-amber-300 font-mono">{row.user_id.slice(0, 18)}…</span>
                            ) : (
                              <span className="text-white/40">— (orphaned)</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {lookup.pt_user && s.rows_to_link > 0 && (
                <button
                  type="button"
                  onClick={confirmRelink}
                  disabled={busy}
                  data-testid="lumi-relink-confirm"
                  className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
                >
                  {busy ? "Linking…" : `🔗 Link ${s.rows_to_link} row(s) → ${lookup.google_email}`}
                </button>
              )}
              {lookup.pt_user && s.rows_to_link === 0 && s.rows_already_linked > 0 && (
                <p className="text-emerald-300 text-xs text-center">✓ All credit rows are already linked to this family.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, good, warn, highlight }) {
  const color = good
    ? "text-emerald-300"
    : warn
    ? "text-amber-300"
    : highlight
    ? "text-sky-300"
    : "text-white";
  return (
    <div className="bg-black/30 border border-white/10 rounded-lg px-2 py-2">
      <div className={`text-lg font-bold ${color}`}>{value ?? 0}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-wide">{label}</div>
    </div>
  );
}



// 🌍 Visitors tab — lightweight, privacy-friendly view of real Poodle Talk
// users (anyone who chatted at least once). Excludes Louise's own IPs and
// the synthetic test ranges so it shows REAL traffic only.
function VisitorsTab() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    lumiFetch(`/admin/visitors?range=${range}`)
      .then((r) => alive && setData(r))
      .catch((e) => alive && setError(e?.data?.detail || "Couldn't load visitors"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range]);

  const ranges = [
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "7d", label: "Last 7 days" },
    { id: "14d", label: "Last 14 days" },
    { id: "30d", label: "Last 30 days" },
    { id: "all", label: "All time" },
  ];

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-6xl mx-auto w-full" data-testid="lumi-visitors-tab">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold">Visitors 🌍</h2>
          <p className="text-xs text-white/40 mt-1">Real Poodle Talk users who sent at least one message. Your own traffic is filtered out.</p>
          <p className="text-xs text-white/40 mt-1" data-testid="visitors-tap-tracking-note">Promotion started 15 May 2026; tap tracking deployed 22 May 2026. Earlier visitors will show 0 even if they tapped cards.</p>
        </div>
      </div>

      {/* Range buttons */}
      <div className="flex flex-wrap gap-2 mb-5">
        {ranges.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setRange(r.id)}
            data-testid={`visitors-range-${r.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              range === r.id
                ? "bg-white text-black"
                : "bg-white/10 text-white/70 hover:bg-white/20"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-400/30 text-red-200 rounded-lg p-3 text-xs mb-4">{error}</div>
      )}

      {loading ? (
        <p className="text-white/40 text-sm">Loading…</p>
      ) : data ? (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Stat label="Visitors" value={data.total_visitors} highlight={data.total_visitors > 0} />
            <Stat label="AI messages" value={data.total_messages} />
            <Stat label="Card taps" value={data.total_taps || 0} />
            <Stat label="Tap-only kids" value={data.tap_only_visitors || 0} good={data.tap_only_visitors > 0} />
            <Stat label="💛 Paying" value={data.paying_count} good />
          </div>

          {/* Top countries */}
          {data.top_countries && data.top_countries.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-5">
              <p className="text-xs text-white/60 uppercase tracking-wide mb-3">Top countries</p>
              <div className="space-y-1.5">
                {data.top_countries.slice(0, 10).map(([country, count]) => {
                  const pct = Math.round((count / data.total_visitors) * 100);
                  return (
                    <div key={country} className="flex items-center gap-2 text-sm">
                      <span className="w-32 truncate text-white/80">{country}</span>
                      <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                        <div className="bg-emerald-400/70 h-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-right text-white/60 text-xs">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Research panel: communication preferences (anonymous, no chat content). */}
          {(data.top_categories?.length > 0 || data.top_emotions?.length > 0 || data.age_mode_breakdown?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              <ResearchBars title="Picture card categories" emoji="🃏" rows={data.top_categories} />
              <ResearchBars title="Emotions selected" emoji="🎭" rows={data.top_emotions} />
              <ResearchBars title="Age mode" emoji="👶" rows={data.age_mode_breakdown} />
            </div>
          )}

          {/* Visitor table */}
          {data.visitors && data.visitors.length > 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-white/40 uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Country</th>
                      <th className="text-left px-3 py-2">City</th>
                      <th className="text-left px-3 py-2">IP (masked)</th>
                      <th className="text-left px-3 py-2" title="AI chat messages sent to Claude">AI msgs</th>
                      <th className="text-left px-3 py-2" title="Picture card / emotion / quick-message taps (no AI cost)">Taps</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.visitors.map((v, i) => {
                      const tapOnly = (v.engagement_taps || 0) > 0 && (v.messages_sent || 0) === 0;
                      return (
                      <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                        <td className="px-3 py-2 text-white/80">{fmtTime(v.last_activity)}</td>
                        <td className="px-3 py-2">
                          {v.country_code && (
                            <span className="mr-1.5 font-mono text-white/40 text-[10px]">{v.country_code}</span>
                          )}
                          {v.country || "Unknown"}
                        </td>
                        <td className="px-3 py-2 text-white/60">{v.city || "—"}</td>
                        <td className="px-3 py-2 font-mono text-white/40">{v.ip_masked}</td>
                        <td className="px-3 py-2 font-semibold">{v.messages_sent}</td>
                        <td className="px-3 py-2 font-semibold">
                          {v.engagement_taps || 0}
                          {tapOnly && (
                            <span className="ml-1.5 text-emerald-300 text-[10px]" title="This visitor only used the picture-card / emotion / quick-message interface — useful AAC / SEND signal.">
                              tap-only
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {v.is_paying ? (
                            <span className="text-emerald-300">💛 Paying</span>
                          ) : v.is_signed_in ? (
                            <span className="text-sky-300">🔑 Signed-in</span>
                          ) : (
                            <span className="text-white/40">Guest</span>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-white/40">
              <p className="text-3xl mb-2">🐩</p>
              <p className="text-sm">No outside visitors in this range yet.</p>
              <p className="text-xs mt-1">Once a real family chats, they'll show up here.</p>
            </div>
          )}

          <p className="text-[10px] text-white/30 mt-4 leading-relaxed">
            Privacy: only the first 3 of 4 IP digits are shown. Your own IP (82.33.186.17) and test traffic are excluded. Country/city looked up via ip-api.com and cached locally.
          </p>
        </>
      ) : null}
    </div>
  );
}


// 📋 BroadcastPicker — replaces the wall of "Broadcast to <list>" buttons.
// Default state: a single neat dropdown trigger.
// Open state: search + scrollable list, click a row to broadcast / schedule.
function BroadcastPicker({ lists, onBroadcast, onSchedule }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? lists
    : lists.filter((l) => (l.name || "").toLowerCase().includes(q));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lumi-broadcast-picker-toggle"
        className="text-xs bg-white/10 hover:bg-white/15 border border-white/15 rounded-full px-4 py-2 font-medium inline-flex items-center gap-2"
      >
        🚀 Broadcast to a list…
        <span className="text-white/50">({lists.length})</span>
        <span className="text-white/40 text-[10px]">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div
          className="absolute z-30 mt-2 right-0 w-80 bg-[#1a1a1a] border border-white/15 rounded-xl shadow-2xl p-2"
          data-testid="lumi-broadcast-picker-panel"
        >
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search lists…"
            autoFocus
            data-testid="lumi-broadcast-picker-search"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-white/30 mb-2"
          />
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-white/40 px-3 py-3 text-center">No lists match.</p>
            ) : (
              filtered.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center justify-between hover:bg-white/5 rounded-lg px-2 py-1.5"
                >
                  <button
                    type="button"
                    onClick={() => { onBroadcast(l.id); setOpen(false); }}
                    data-testid={`lumi-broadcast-${l.id}`}
                    className="text-xs text-left flex-1 truncate"
                    title="Send now"
                  >
                    🚀 {l.name}
                    <span className="text-white/40 ml-1">({l.contact_count})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { onSchedule(l.id); setOpen(false); }}
                    data-testid={`lumi-schedule-${l.id}`}
                    title="Schedule for later"
                    className="text-xs hover:bg-white/15 rounded px-2 py-1 ml-2"
                  >
                    ⏰
                  </button>
                </div>
              ))
            )}
          </div>
          <p className="text-[10px] text-white/30 mt-2 px-2">Tip: 🚀 sends now · ⏰ picks a date/time</p>
        </div>
      )}
    </div>
  );
}


// 📊 ResearchBars — tiny stacked bar list used in the Visitors tab to show
// breakdowns like "which picture-card categories are most-tapped",
// "which emotions are most chosen", "age-mode mix".
// Anonymous aggregates only — never per-child data.
function ResearchBars({ title, emoji, rows }) {
  const total = (rows || []).reduce((s, [, v]) => s + v, 0);
  if (!rows || rows.length === 0 || total === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <p className="text-xs text-white/60 uppercase tracking-wide mb-2">{emoji} {title}</p>
        <p className="text-xs text-white/30">No data yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <p className="text-xs text-white/60 uppercase tracking-wide mb-3">{emoji} {title}</p>
      <div className="space-y-1.5">
        {rows.slice(0, 8).map(([label, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={label} className="flex items-center gap-2 text-xs">
              <span className="w-24 truncate text-white/80 capitalize">{label}</span>
              <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                <div className="bg-pink-400/70 h-full" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-10 text-right text-white/60">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

